import React, { useRef, useState, useCallback } from 'react';
import { Printer, ArrowLeft } from 'lucide-react';

/**
 * Mimic — Data Analyst Case: yontem ve bulgular.
 * Sidebar YOK (App.js'te ust seviye route). Sadece allowEmails ile acilir.
 * Rapor iframe srcDoc icinde: uygulama CSS'i ile karsilikli sizinti olmaz,
 * "PDF indir" tarayicinin print motorunu cagirir -> metni secilebilir vektorel PDF.
 */

const REPORT_HTML = `<!doctype html>
<html lang="tr" data-theme="light">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Mimic Data Analyst Case — Çözüm Planı</title>
<style>
:root{
  --ground:#F2F5F6; --surface:#FFFFFF; --sunk:#E9EEF0;
  --ink:#111A1E; --ink-2:#3D4E55; --ink-3:#6D8087; --hair:#D7E0E3; --hair-2:#C2CFD3;
  --accent:#0C5C6B; --accent-soft:#0C5C6B1F; --accent-line:#0C5C6B4D;
  --ochre:#8A5310; --ochre-bg:#8A53101A;
  --alert:#93261E; --alert-bg:#93261E14;
  --ok:#2A6551; --ok-bg:#2A65511A;
  --shadow:0 1px 2px #0f1d2210, 0 8px 24px -14px #0f1d2233;
}
@media (prefers-color-scheme:dark){
  :root{
    --ground:#0C1215; --surface:#131C20; --sunk:#0A0F12;
    --ink:#E3EAEC; --ink-2:#A9BBC1; --ink-3:#7C8F96; --hair:#223036; --hair-2:#2E3F46;
    --accent:#5FB9C7; --accent-soft:#5FB9C726; --accent-line:#5FB9C759;
    --ochre:#D69C50; --ochre-bg:#D69C501F;
    --alert:#E28074; --alert-bg:#E280741A;
    --ok:#71BC98; --ok-bg:#71BC981A;
    --shadow:0 1px 2px #0006, 0 10px 28px -16px #000a;
  }
}
:root[data-theme="light"]{
  --ground:#F2F5F6; --surface:#FFFFFF; --sunk:#E9EEF0;
  --ink:#111A1E; --ink-2:#3D4E55; --ink-3:#6D8087; --hair:#D7E0E3; --hair-2:#C2CFD3;
  --accent:#0C5C6B; --accent-soft:#0C5C6B1F; --accent-line:#0C5C6B4D;
  --ochre:#8A5310; --ochre-bg:#8A53101A;
  --alert:#93261E; --alert-bg:#93261E14;
  --ok:#2A6551; --ok-bg:#2A65511A;
  --shadow:0 1px 2px #0f1d2210, 0 8px 24px -14px #0f1d2233;
}
:root[data-theme="dark"]{
  --ground:#0C1215; --surface:#131C20; --sunk:#0A0F12;
  --ink:#E3EAEC; --ink-2:#A9BBC1; --ink-3:#7C8F96; --hair:#223036; --hair-2:#2E3F46;
  --accent:#5FB9C7; --accent-soft:#5FB9C726; --accent-line:#5FB9C759;
  --ochre:#D69C50; --ochre-bg:#D69C501F;
  --alert:#E28074; --alert-bg:#E280741A;
  --ok:#71BC98; --ok-bg:#71BC981A;
  --shadow:0 1px 2px #0006, 0 10px 28px -16px #000a;
}

*{box-sizing:border-box}
body{
  margin:0; background:var(--ground); color:var(--ink);
  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;
  font-size:16px; line-height:1.6; -webkit-font-smoothing:antialiased;
}
.serif{font-family:"Iowan Old Style","Palatino Linotype",Palatino,"Book Antiqua",Georgia,serif}
.mono{font-family:ui-monospace,"SF Mono",SFMono-Regular,Menlo,Consolas,"Liberation Mono",monospace}

.wrap{max-width:920px; margin:0 auto; padding:0 24px 96px}
.col{max-width:68ch}

/* ---- header ---- */
header{padding:64px 0 36px; border-bottom:1px solid var(--hair)}
.ruler{display:flex; gap:3px; align-items:flex-end; height:16px; margin-bottom:26px}
.ruler i{width:2px; background:var(--hair-2); height:6px; display:block}
.ruler i:nth-child(4n+1){height:14px; background:var(--accent-line)}
h1{
  font-family:"Iowan Old Style","Palatino Linotype",Palatino,"Book Antiqua",Georgia,serif;
  font-size:clamp(30px,5.2vw,46px); line-height:1.12; margin:0 0 14px; font-weight:600;
  letter-spacing:-.015em; text-wrap:balance;
}
h1 em{font-style:italic; color:var(--accent)}
.lede{font-size:17.5px; color:var(--ink-2); margin:0; max-width:60ch}
.eyebrow{
  font-size:11.5px; font-weight:600; letter-spacing:.13em; text-transform:uppercase;
  color:var(--ink-3); margin:0 0 10px;
}
.metastrip{
  display:flex; flex-wrap:wrap; gap:0; margin-top:30px;
  border:1px solid var(--hair); border-radius:3px; background:var(--surface); overflow:hidden;
}
.metastrip div{flex:1 1 160px; padding:13px 16px; border-right:1px solid var(--hair)}
.metastrip div:last-child{border-right:0}
.metastrip dt{font-size:11px; letter-spacing:.09em; text-transform:uppercase; color:var(--ink-3); margin:0 0 3px}
.metastrip dd{margin:0; font-size:15px; font-weight:600; font-variant-numeric:tabular-nums}

/* ---- sections ---- */
section{padding:52px 0 0; border-top:1px solid var(--hair); margin-top:52px}
section:first-of-type{border-top:0}
h2{
  font-family:"Iowan Old Style","Palatino Linotype",Palatino,"Book Antiqua",Georgia,serif;
  font-size:clamp(23px,3.4vw,31px); line-height:1.2; margin:0 0 16px; font-weight:600;
  letter-spacing:-.01em; text-wrap:balance;
}
h3{font-size:15px; font-weight:650; margin:34px 0 12px; letter-spacing:-.005em}
h4{font-size:13px; font-weight:650; margin:22px 0 8px; color:var(--ink-2)}
p{margin:0 0 14px}
a{color:var(--accent); text-decoration-thickness:1px; text-underline-offset:2px}
a:focus-visible,button:focus-visible{outline:2px solid var(--accent); outline-offset:3px; border-radius:2px}

.qbox{
  background:var(--surface); border:1px solid var(--hair); border-left:3px solid var(--accent);
  border-radius:3px; padding:18px 22px; margin:0 0 30px; box-shadow:var(--shadow);
}
.qbox p:last-child{margin-bottom:0}
.qbox .eyebrow{color:var(--accent)}
.qbox ol{margin:8px 0 0; padding-left:20px}
.qbox li{margin-bottom:5px}

/* ---- steps ---- */
.steps{list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:2px}
.steps li{display:flex; gap:16px; padding:15px 0; border-top:1px solid var(--hair)}
.steps li:last-child{border-bottom:1px solid var(--hair)}
.steps .n{
  flex:0 0 26px; height:26px; border:1px solid var(--accent-line); border-radius:50%;
  display:grid; place-items:center; font-size:12px; font-weight:650; color:var(--accent);
  font-variant-numeric:tabular-nums; margin-top:1px;
}
.steps .b{flex:1 1 auto; min-width:0}
.steps b{display:block; font-size:15px; font-weight:650; margin-bottom:3px}
.steps p{font-size:14.5px; color:var(--ink-2); margin:0}
.steps code{font-size:13px}

code{
  font-family:ui-monospace,"SF Mono",SFMono-Regular,Menlo,Consolas,"Liberation Mono",monospace;
  background:var(--sunk); padding:1px 5px; border-radius:2px; font-size:13.5px;
}
pre{
  background:var(--sunk); border:1px solid var(--hair); border-radius:3px;
  padding:16px 18px; overflow-x:auto; margin:14px 0 20px;
  font-family:ui-monospace,"SF Mono",SFMono-Regular,Menlo,Consolas,"Liberation Mono",monospace;
  font-size:13px; line-height:1.65; color:var(--ink-2);
}
pre b{color:var(--ink); font-weight:650}
pre .c{color:var(--ink-3); font-style:italic}

/* ---- data cards ---- */
.cards{display:grid; grid-template-columns:repeat(auto-fit,minmax(300px,1fr)); gap:18px; margin:24px 0 0}
.card{
  background:var(--surface); border:1px solid var(--hair); border-radius:3px;
  padding:20px 22px; box-shadow:var(--shadow);
}
.card h3{margin:0 0 2px; font-size:15px}
.card .file{
  font-family:ui-monospace,"SF Mono",SFMono-Regular,Menlo,Consolas,monospace;
  font-size:11.5px; color:var(--ink-3); word-break:break-all; display:block; margin-bottom:14px;
}
.kv{display:grid; grid-template-columns:auto 1fr; gap:5px 14px; font-size:13.5px; margin:0}
.kv dt{color:var(--ink-3)}
.kv dd{margin:0; font-variant-numeric:tabular-nums; text-align:right;
  font-family:ui-monospace,"SF Mono",SFMono-Regular,Menlo,Consolas,monospace; font-size:13px}

/* ---- tables ---- */
.tw{overflow-x:auto; margin:16px 0 22px; border:1px solid var(--hair); border-radius:3px; background:var(--surface)}
table{border-collapse:collapse; width:100%; font-size:13.5px}
th,td{padding:9px 14px; text-align:left; border-bottom:1px solid var(--hair); white-space:nowrap}
thead th{
  font-size:11px; letter-spacing:.08em; text-transform:uppercase; color:var(--ink-3);
  font-weight:650; background:var(--sunk); position:sticky; top:0;
}
tbody tr:last-child td{border-bottom:0}
td.num,th.num{text-align:right; font-variant-numeric:tabular-nums;
  font-family:ui-monospace,"SF Mono",SFMono-Regular,Menlo,Consolas,monospace; font-size:13px}
tfoot td{font-weight:650; background:var(--sunk); border-top:1px solid var(--hair-2)}

/* ---- flags ---- */
.flag{
  display:flex; gap:12px; padding:14px 16px; border-radius:3px; margin:0 0 10px;
  font-size:14.5px; line-height:1.55; border:1px solid transparent;
}
.flag .t{flex:0 0 auto; font-size:10.5px; font-weight:700; letter-spacing:.09em;
  text-transform:uppercase; padding-top:3px; min-width:64px}
.flag p{margin:0}
.flag.warn{background:var(--ochre-bg); border-color:#8a53103d} .flag.warn .t{color:var(--ochre)}
.flag.stop{background:var(--alert-bg); border-color:#93261e33} .flag.stop .t{color:var(--alert)}
.flag.good{background:var(--ok-bg); border-color:#2a655133} .flag.good .t{color:var(--ok)}
@media (prefers-color-scheme:dark){
  .flag.warn{border-color:#d69c5040} .flag.stop{border-color:#e2807440} .flag.good{border-color:#71bc9840}
}

/* ---- result ---- */
.result{
  display:flex; flex-wrap:wrap; gap:0; border:1px solid var(--accent-line);
  border-radius:3px; overflow:hidden; margin:26px 0 8px; background:var(--surface);
}
.result div{flex:1 1 150px; padding:18px 20px; border-right:1px solid var(--hair)}
.result div:last-child{border-right:0}
.result .lab{font-size:11px; letter-spacing:.09em; text-transform:uppercase; color:var(--ink-3); margin-bottom:6px}
.result .big{
  font-size:31px; font-weight:600; line-height:1; letter-spacing:-.02em; color:var(--accent);
  font-variant-numeric:tabular-nums;
  font-family:"Iowan Old Style","Palatino Linotype",Palatino,Georgia,serif;
}
.result .sub{font-size:12.5px; color:var(--ink-3); margin-top:6px}
.caption{font-size:12.5px; color:var(--ink-3); margin:8px 0 0; max-width:64ch}

/* ---- chart ---- */
figure{margin:26px 0 0; background:var(--surface); border:1px solid var(--hair);
  border-radius:3px; padding:20px 20px 14px; box-shadow:var(--shadow); position:relative}
figcaption{font-size:14px; font-weight:650; margin-bottom:2px}
.figsub{font-size:12.5px; color:var(--ink-3); margin-bottom:14px}
.legend{display:flex; gap:18px; font-size:12.5px; color:var(--ink-2); margin-top:10px; flex-wrap:wrap}
.legend span{display:flex; align-items:center; gap:7px}
.legend .sw{width:16px; height:0; border-top:2px solid var(--accent)}
.legend .sw.d{border-top-style:dashed}
#chart{display:block; width:100%; height:auto; touch-action:none}
#tip{
  position:absolute; pointer-events:none; opacity:0; transition:opacity .1s;
  background:var(--surface); border:1px solid var(--hair-2); border-radius:3px;
  padding:7px 11px; font-size:12.5px; box-shadow:var(--shadow); white-space:nowrap; z-index:5;
  font-variant-numeric:tabular-nums;
}
#tip b{display:block; font-size:11px; color:var(--ink-3); font-weight:600;
  letter-spacing:.06em; text-transform:uppercase; margin-bottom:2px}

/* ---- lineage / join mapping ---- */
.lineage{
  display:grid; grid-template-columns:1fr 116px 1fr 138px 1fr; align-items:stretch; gap:0;
  margin:22px 0 6px;
}
.tbl{
  background:var(--surface); border:1px solid var(--hair); border-radius:3px;
  box-shadow:var(--shadow); display:flex; flex-direction:column; overflow:hidden;
}
.tbl.mid{border-color:var(--accent-line); border-width:1.5px}
.tbl header{padding:0; border:0; margin:0; background:var(--sunk); border-bottom:1px solid var(--hair)}
.thead{padding:11px 14px 10px}
.pfx{display:block; color:var(--ink-3); font-weight:400}
.tname{
  font-family:ui-monospace,"SF Mono",SFMono-Regular,Menlo,Consolas,monospace;
  font-size:12.5px; font-weight:650; color:var(--ink); display:block; word-break:break-all; line-height:1.35;
}
.grain{
  display:inline-block; margin-top:7px; font-size:10.5px; letter-spacing:.07em; text-transform:uppercase;
  color:var(--accent); background:var(--accent-soft); padding:2px 7px; border-radius:2px; font-weight:650;
}
.rowcount{font-size:11.5px; color:var(--ink-3); margin-top:6px; font-variant-numeric:tabular-nums}
.fields{list-style:none; margin:0; padding:6px 0; flex:1 1 auto}
.fields li{
  display:flex; justify-content:space-between; align-items:baseline; gap:8px;
  padding:4px 14px; font-size:12.5px;
  font-family:ui-monospace,"SF Mono",SFMono-Regular,Menlo,Consolas,monospace; color:var(--ink-3);
}
.fields li.key{color:var(--accent); font-weight:650; background:var(--accent-soft)}
.fields li.msr{color:var(--ink)}
.ftag{
  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;
  font-size:9.5px; letter-spacing:.08em; text-transform:uppercase; color:var(--ink-3); flex:0 0 auto;
}
.fields li.key .ftag{color:var(--accent)}
.link{display:flex; flex-direction:column; align-items:center; justify-content:center; gap:5px; padding:0 6px; text-align:center}
.lkey{
  font-family:ui-monospace,"SF Mono",SFMono-Regular,Menlo,Consolas,monospace;
  font-size:11px; color:var(--accent); font-weight:650; line-height:1.35; word-break:break-word;
}
.arrow{width:100%; height:11px; display:block}
.lcard{font-size:10.5px; letter-spacing:.07em; text-transform:uppercase; color:var(--ink-3)}
.lnote{font-size:11px; color:var(--ochre); line-height:1.35; margin-top:1px}
@media(max-width:760px){
  .lineage{grid-template-columns:1fr; gap:0}
  .link{padding:14px 0; flex-direction:row; flex-wrap:wrap; gap:8px}
  .arrow{width:34px; transform:rotate(90deg)}
}

/* ---- funnel ---- */
.funnel{display:flex; flex-direction:column; gap:1px}
.fstep{
  display:grid; grid-template-columns:190px 1fr 62px 56px; align-items:center; gap:14px;
  padding:9px 0; border-bottom:1px solid var(--hair);
}
.fstep:last-child{border-bottom:0}
.fl{font-size:13.5px; color:var(--ink-2)}
.fbar{
  height:15px; border-radius:0 3px 3px 0; background:var(--accent);
  width:var(--w); min-width:3px; display:block;
}
.fstep:first-child .fbar{opacity:.34}
.fv{
  text-align:right; font-variant-numeric:tabular-nums; font-weight:650; font-size:14px;
  font-family:ui-monospace,"SF Mono",SFMono-Regular,Menlo,Consolas,monospace;
}
.fp{text-align:right; font-variant-numeric:tabular-nums; font-size:13px; color:var(--ink-3);
  font-family:ui-monospace,"SF Mono",SFMono-Regular,Menlo,Consolas,monospace}
@media(max-width:560px){
  .fstep{grid-template-columns:1fr 58px 50px; gap:8px}
  .fl{grid-column:1/-1; margin-bottom:-4px}
}
ul.plain{margin:0 0 16px; padding-left:20px}
ul.plain li{margin-bottom:7px}
ul.plain li::marker{color:var(--ink-3)}

.deliver{
  border:1px dashed var(--hair-2); border-radius:3px; padding:20px 22px; margin-top:24px;
  background:var(--surface);
}
footer{margin-top:56px; padding-top:22px; border-top:1px solid var(--hair);
  font-size:13px; color:var(--ink-3)}
@media(prefers-reduced-motion:reduce){*{transition:none!important; animation:none!important}}
</style>

<style>
@page{size:A4;margin:14mm 12mm}
@media print{
  body{background:#fff !important;font-size:14px}
  .wrap{max-width:none;padding:0 0 8px}
  header{padding-top:0}
  h2,h3,h4{page-break-after:avoid;break-after:avoid}
  figure,.result,.lineage,.tw,.qbox,.flag,.deliver,.card,.steps li,.metastrip{page-break-inside:avoid;break-inside:avoid}
  pre{page-break-inside:avoid;break-inside:avoid;font-size:11px;line-height:1.55}
  .tw{overflow:visible}
  table{font-size:11px}
  th,td{padding:6px 9px}
  .lineage{grid-template-columns:1fr 92px 1fr 108px 1fr}
  .fields li{font-size:10.5px;padding:3px 11px}
  .tname{font-size:11px}
  section{padding-top:34px;margin-top:34px}
}
</style>
</head>
<body>
<div class="wrap">

<header>
  <div class="ruler" aria-hidden="true">
    <i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i>
    <i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i>
    <i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i>
  </div>
  <p class="eyebrow">Data Analyst Recruitment Case &middot; Mimic</p>
  <h1>Üç soru, dört dosya,<br><em>iki gerçek cevap</em></h1>
  <p class="lede">Vaka bizden hesap değil <strong>yaklaşım</strong> istiyor. Aşağıda her sorunun ne sorduğu, veriden fiilen çıkan cevap ve o cevaba nasıl vardığımız var. 1. ve 2. soru elimizdeki dosyalarla <strong>tam olarak çözüldü</strong>; 3. soru için gereken event dosyası hâlâ eksik.</p>
  <p class="caption" style="margin-top:14px">30 Temmuz revizyonu: 1. sorunun LTV'si tek dosya üzerinden yeniden kuruldu, <strong>$31,99 &rarr; $29,12</strong>. Gerekçeler ve iki payda senaryosu <a href="#revizyon">Soru 1 &rsaquo; Revizyon</a> bölümünde. Aşağıdaki Soru 1 metni ve Soru 2'nin ROAS rakamları hâlâ revizyon öncesi $31,99'a dayanıyor.</p>

  <dl class="metastrip">
    <div><dt>1 yıllık LTV</dt><dd>$29,12 / abone <span style="color:var(--ink-3);font-weight:400">(rev.)</span></dd></div>
    <div><dt>En kârlı kampanya</dt><dd>MMC_107 &middot; 2,23× ROAS</dd></div>
    <div><dt>En düşük CPI</dt><dd>MMC_133 &middot; $0,19</dd></div>
    <div><dt>Cevaplanan</dt><dd>2 / 3 soru</dd></div>
  </dl>
</header>

<!-- ============ VERİ ============ -->
<section>
  <p class="eyebrow">Başlangıç noktası</p>
  <h2>Elimizde ne var</h2>
  <div class="col">
    <p>Dört dosyayı satır satır profilledim. 1. soru kendi tablosuyla çözülüyor; 2. sorunun üç tablosu birbirine <code>user_id</code> ve <code>campaign</code> üzerinden bağlanıyor. 3. sorunun event dosyası klasörde yok.</p>
  </div>

  <div class="cards">
    <div class="card">
      <h3>Abonelik işlemleri <span style="color:var(--ink-3);font-weight:400">· S1</span></h3>
      <code class="file">mimic_weekly_sub_pro_transactions2.csv</code>
      <dl class="kv">
        <dt>Satır / kullanıcı</dt><dd>4.225 / 946</dd>
        <dt>Tarih aralığı</dt><dd>01 Ağu 22 → 12 Oca 23</dd>
        <dt>Ödeme olayı</dt><dd>3.553</dd>
        <dt>İptal olayı</dt><dd>672</dd>
        <dt>Toplam gelir</dt><dd>$17.731,48</dd>
        <dt>Ödeme başına</dt><dd>$4,99</dd>
      </dl>
    </div>
    <div class="card">
      <h3>Kurulumlar <span style="color:var(--ink-3);font-weight:400">· S2</span></h3>
      <code class="file">case_study_install_dataset.csv</code>
      <dl class="kv">
        <dt>Satır = tekil kullanıcı</dt><dd>2.250</dd>
        <dt>Tarih aralığı</dt><dd>01 → 05 Oca 23</dd>
        <dt>Kanal</dt><dd>Facebook 1.723 · TikTok 527</dd>
        <dt>Kampanya (ham)</dt><dd>16 değer</dd>
        <dt>Kampanya (normalize)</dt><dd>11 + boş</dd>
        <dt>Organik satır</dt><dd>yok</dd>
      </dl>
    </div>
    <div class="card">
      <h3>Gelir olayları <span style="color:var(--ink-3);font-weight:400">· S2</span></h3>
      <code class="file">case_study_revenue_dataset.csv</code>
      <dl class="kv">
        <dt>Satır / kullanıcı</dt><dd>686 / 326</dd>
        <dt>Olay tarihi</dt><dd>01 → 16 Oca 23</dd>
        <dt>Ürün</dt><dd>yıllık 3g trial · haftalık</dd>
        <dt>trial_started</dt><dd>261</dd>
        <dt>trial_converted</dt><dd>69</dd>
        <dt>Toplam gelir</dt><dd>$3.010,72</dd>
      </dl>
    </div>
    <div class="card">
      <h3>Kampanya maliyeti <span style="color:var(--ink-3);font-weight:400">· S2</span></h3>
      <code class="file">case_study_cost_dataset.csv</code>
      <dl class="kv">
        <dt>Satır</dt><dd>20</dd>
        <dt>Tarih aralığı</dt><dd>01 → 05 Oca 23</dd>
        <dt>Kampanya (normalize)</dt><dd>3</dd>
        <dt>Toplam harcama</dt><dd>$1.524,93</dd>
        <dt>Toplam gösterim</dt><dd>309.466</dd>
        <dt>Ortalama CPM</dt><dd>$4,93</dd>
      </dl>
    </div>
  </div>

  <div style="margin-top:26px">
    <div class="flag stop">
      <span class="t">Eksik</span>
      <p><strong>3. soru (churn bonusu)</strong> için 1–5 Kasım 2022 kurulumlarının event verisi gerekiyor; o dosya klasörde yok. <strong>D1/D7 retention bu veriyle hesaplanamaz</strong> — elimizdeki üç S2 tablosunda uygulama açılışı, oturum veya ekran görüntüleme olayı yok, sadece para olayları var. Aşağıda bunun yerine <em>para hunisi</em> üzerinden yapılabilen churn analizini uyguladım.</p>
    </div>
    <div class="flag warn">
      <span class="t">Dikkat</span>
      <p>S1 ve S2 <strong>aynı döneme ve aynı ürüne ait değil</strong>: S1 Ağu–Ara 2022, ürün <code>mimic_weekly_sub_pro</code>; S2 Oca 2023, ürünler <code>mimic_pro_weekly_2</code> ve <code>mimic_pro_yearly_3d_trial</code>. Fiyat aynı ($4,99) ama SKU farklı. S1'in LTV'sini S2'ye taşımak <em>meşru bir varsayım</em>, ama varsayım olduğu yazılmalı.</p>
    </div>
  </div>
</section>

<!-- ============ SORU 1 ============ -->
<section>
  <p class="eyebrow">Soru 1 &middot; Ana soru</p>
  <h2>Abone başına 1 yıllık tahmini LTV</h2>

  <div class="qbox">
    <p class="eyebrow">Vakanın sorduğu</p>
    <p><code>mimic_weekly_sub_pro</code> haftalık aboneliğine 4 ay içinde en az bir kez abone olmuş ABD kullanıcılarının işlem verisinden yola çıkarak, <strong>ortalama bir abone için 1 yıllık tahmini LTV'yi</strong> hesaplayacak bir yöntem geliştir ve sonucu paylaş. <span style="color:var(--ink-3)">İpucu: geçmiş yenileme oranını kullan.</span></p>
  </div>

  <div class="col">
    <p>Bu sorunun asıl sınavı aritmetik değil: <strong>kesilmiş (censored) veriyle ne yaptığın</strong>. Aralık ayında abone olan bir kullanıcının verisi 12 Ocak'ta bitiyor — o kullanıcı 3. haftasında “kaybedilmiş” değil, sadece henüz gözlenmemiş. Naif bir “toplam yenileme / toplam abone” oranı bu yüzden LTV'yi ciddi biçimde küçük gösterir.</p>
    <p>Yöntemimiz: <strong>dönem bazlı koşullu yenileme oranı (discrete-time survival) + risk seti düzeltmesi + 52 haftaya ekstrapolasyon.</strong></p>
  </div>

  <h3>Adımlar</h3>
  <ol class="steps">
    <li><span class="n">1</span><div class="b"><b>Veriyi temizle ve dönemleştir</b>
      <p>Kullanıcı bazında ödeme olaylarını (<code>initial_purchase</code> + <code>renewed</code>) zamana göre sırala; her ödemeye 1'den başlayan bir <em>fatura dönemi</em> numarası ver. <code>subscription_canceled</code> gelir üretmez — sadece iptal sinyalidir, dönem sayılmaz.</p></div></li>
    <li><span class="n">2</span><div class="b"><b>Her dönem için risk setini kur</b>
      <p>Bir kullanıcı <em>n</em>. dönemden <em>n+1</em>'e geçiş hesabına ancak <em>n</em>. ödemesinden sonra veri bitimine kadar en az 7 gün varsa girer. Bu tek kural, kesilmişliği ortadan kaldırır.</p></div></li>
    <li><span class="n">3</span><div class="b"><b>Koşullu yenileme eğrisini çıkar</b>
      <p><code>r(n) = P(n+1. ödeme | n. ödeme yapıldı)</code>. Bu oran sabit değil: ilk haftadan sonra sert düşüyor, sonra sadıklar kaldıkça yükseliyor. Sabit oran varsayan her model burada yanılır.</p></div></li>
    <li><span class="n">4</span><div class="b"><b>52 haftaya uzat</b>
      <p>Risk seti yeterli olan dönemlerde (n ≤ 18, risk seti ≥ 30 kişi) gözlenen oranı kullan; ötesinde plato oranını uygula. Alternatif olarak <strong>sBG (shifted-beta-geometric)</strong> modelini MLE ile fit edip tüm eğriyi tek parametre çiftiyle üretmek — sunumda ikisini yan yana göstermek iyi olur.</p></div></li>
    <li><span class="n">5</span><div class="b"><b>LTV'ye çevir</b>
      <p><code>LTV = ARPP × Σ S(n)</code>, n = 1…52. ARPP = ödeme başına ortalama gelir ($4,99). Hem brüt hem <strong>store komisyonu sonrası net</strong> (×0,70 / ×0,85) versiyonu ver — pazarlama kararında kullanılacak olan nettir.</p></div></li>
    <li><span class="n">6</span><div class="b"><b>Duyarlılık ve segment</b>
      <p>Plato oranını ±%5 oynatıp LTV aralığı sun. Sonra kurulum ayı kohortlarına böl: Ağustos kohortu ile Aralık kohortu aynı eğriye sahip mi? Trafik kalitesi değiştiyse tek bir ortalama LTV yanıltıcıdır.</p></div></li>
  </ol>

  <h3>Veride bulduğum tuzaklar</h3>
  <div class="flag warn"><span class="t">Kesilme</span><p>Kullanıcıların önemli kısmı veri bitimine yakın abone olmuş. Düzeltilmemiş yenileme oranı LTV'yi <strong>düşük</strong> tahmin eder.</p></div>
  <div class="flag warn"><span class="t">Yinelenen</span><p>4.225 satırda yalnız <strong>3.569 tekil <code>purchase_id</code></strong> var. İptal olayı, sonlandırdığı ödemenin <code>purchase_id</code>'sini paylaşıyor; 50 kullanıcıda aynı <code>purchase_id</code> için <strong>birden fazla iptal</strong> kaydı var (birinde 8 kez). Sayım yapmadan önce tekilleştirme şart. Bir <code>purchase_id</code>'de iki <code>renewed</code> satırı da var.</p></div>
  <div class="flag warn"><span class="t">Eksik baş</span><p><strong>84 kullanıcının</strong> <code>initial_purchase</code> kaydı yok (pencere öncesi abone olmuşlar), <strong>34 kullanıcının</strong> hiç ödeme olayı yok — sadece iptal. Bunlar LTV paydasına girerse ortalamayı bozar; ayrı ele alınmalı.</p></div>
  <div class="flag warn"><span class="t">Tarih</span><p>11 kullanıcıda <code>install_date</code>, ilk satın almadan <em>sonra</em> (−62 güne kadar). Kohort analizini install tarihine dayandırırken bu satırları işaretle.</p></div>
  <div class="flag good"><span class="t">Temiz</span><p>Faturalama ritmi sağlam: ardışık ödemeler arasındaki farkın <strong>2.563'ü tam 7 gün</strong>. Fiyat da sabit — 3.553 ödemenin 3.541'i tam $4,99, kalanı $4,60–$6,03 arası kur/promosyon sapması. Haftalık dönem varsayımı güvenli.</p></div>

  <h3>Ön sonuç <span style="font-weight:400;color:var(--ink-3);font-size:13px">— yöntem bu veriye uygulandığında</span></h3>
  <div class="result">
    <div><div class="lab">1. → 2. hafta</div><div class="big">47,6<span style="font-size:19px">%</span></div><div class="sub">tek kritik eşik</div></div>
    <div><div class="lab">Beklenen ödeme</div><div class="big">6,41</div><div class="sub">hafta / abone (52 hf)</div></div>
    <div><div class="lab">LTV brüt</div><div class="big">$31,99</div><div class="sub">$29,87 – $34,03 aralık</div></div>
    <div><div class="lab">LTV net (×0,70)</div><div class="big">$22,39</div><div class="sub">store komisyonu sonrası</div></div>
  </div>
  <p class="caption">Karşılaştırma: veride <em>fiilen gerçekleşmiş</em> gelir kullanıcı başına $19,44 — ama bu rakam kesilmiş, yani gerçek 1 yıllık değerin altında bir taban.</p>

  <figure>
    <figcaption>Haftalık abonelikte hayatta kalma eğrisi</figcaption>
    <p class="figsub">İlk ödemesini yapan 100 abonenin kaçı n. haftada hâlâ ödüyor</p>
    <svg id="chart" viewBox="0 0 720 300" role="img" aria-label="Hayatta kalma eğrisi: 1. haftada %100, 2. haftada %47,6, 8. haftada %23,7, 18. haftada %12, 52. haftada %1,1"></svg>
    <div class="legend">
      <span><i class="sw"></i> Gözlenen (hf 1–18, risk seti ≥ 30)</span>
      <span><i class="sw d"></i> Projeksiyon (plato %93,1)</span>
    </div>
    <div id="tip"></div>
  </figure>

  <h3>Dönem bazlı yenileme oranları</h3>
  <div class="tw">
    <table>
      <thead><tr><th>Geçiş</th><th class="num">Risk setinde</th><th class="num">Yenileyen</th><th class="num">Yenileme oranı</th><th class="num">Kümülatif kalan</th></tr></thead>
      <tbody>
        <tr><td>hafta 1 → 2</td><td class="num">911</td><td class="num">434</td><td class="num">47,6%</td><td class="num">47,6%</td></tr>
        <tr><td>hafta 2 → 3</td><td class="num">431</td><td class="num">358</td><td class="num">83,1%</td><td class="num">39,6%</td></tr>
        <tr><td>hafta 3 → 4</td><td class="num">349</td><td class="num">297</td><td class="num">85,1%</td><td class="num">33,7%</td></tr>
        <tr><td>hafta 4 → 5</td><td class="num">264</td><td class="num">241</td><td class="num">91,3%</td><td class="num">30,7%</td></tr>
        <tr><td>hafta 6 → 7</td><td class="num">181</td><td class="num">163</td><td class="num">90,1%</td><td class="num">24,9%</td></tr>
        <tr><td>hafta 8 → 9</td><td class="num">140</td><td class="num">132</td><td class="num">94,3%</td><td class="num">22,4%</td></tr>
        <tr><td>hafta 12 → 13</td><td class="num">76</td><td class="num">74</td><td class="num">97,4%</td><td class="num">16,9%</td></tr>
        <tr><td>hafta 17 → 18</td><td class="num">30</td><td class="num">28</td><td class="num">93,3%</td><td class="num">12,0%</td></tr>
        <tr><td>hafta 18+ (plato)</td><td class="num">&lt; 30</td><td class="num">—</td><td class="num">93,1%</td><td class="num">projeksiyon</td></tr>
      </tbody>
    </table>
  </div>
  <p class="caption">Sunumda anlatılacak asıl hikâye bu tabloda: kayıp neredeyse tamamen ilk yenilemede yaşanıyor. İlk haftayı geçen abonenin haftalık kalma olasılığı %85–95 bandına oturuyor. Yani ürün tarafında tek bir kaldıraç var — <strong>1. haftadan 2. haftaya geçiş.</strong></p>

  <h3>Sorgu iskeleti</h3>
  <pre><span class="c">-- 1) kullanıcı × dönem tablosu</span>
<b>WITH</b> paid <b>AS</b> (
  <b>SELECT DISTINCT</b> user_id, purchase_id, event_timestamp
  <b>FROM</b> transactions
  <b>WHERE</b> event_name <b>IN</b> ('initial_purchase','renewed')   <span class="c">-- iptal gelir değil</span>
),
seq <b>AS</b> (
  <b>SELECT</b> user_id, event_timestamp,
         <b>ROW_NUMBER</b>() <b>OVER</b> (<b>PARTITION BY</b> user_id <b>ORDER BY</b> event_timestamp) <b>AS</b> period
  <b>FROM</b> paid
),
<span class="c">-- 2) risk seti: n. ödemeden sonra 7 gün gözlem penceresi kaldı mı?</span>
risk <b>AS</b> (
  <b>SELECT</b> period,
         <b>COUNT</b>(*)                                        <b>AS</b> at_risk,
         <b>COUNTIF</b>(next_ts <b>IS NOT NULL</b>)                    <b>AS</b> renewed
  <b>FROM</b> (
    <b>SELECT</b> s.*, <b>LEAD</b>(event_timestamp) <b>OVER</b> (<b>PARTITION BY</b> user_id <b>ORDER BY</b> period) <b>AS</b> next_ts
    <b>FROM</b> seq s
  )
  <b>WHERE</b> <b>TIMESTAMP_ADD</b>(event_timestamp, <b>INTERVAL</b> 7 <b>DAY</b>) &lt;= (<b>SELECT MAX</b>(event_timestamp) <b>FROM</b> paid)
  <b>GROUP BY</b> period
)
<span class="c">-- 3) kümülatif hayatta kalma ve LTV</span>
<b>SELECT</b> period,
       renewed / at_risk <b>AS</b> renewal_rate,
       <b>EXP</b>(<b>SUM</b>(<b>LN</b>(renewed / at_risk)) <b>OVER</b> (<b>ORDER BY</b> period)) <b>AS</b> survival
<b>FROM</b> risk <b>ORDER BY</b> period;</pre>

  <!-- ---------- REVIZYON: tek dosya + iki senaryo ---------- -->
  <h3 id="revizyon">Revizyon &middot; 30 Temmuz <span style="font-weight:400;color:var(--ink-3);font-size:13px">— tek dosya, iki senaryo</span></h3>
  <div class="col">
    <p>Yukarıdaki $31,99, analiz <strong>yalnızca</strong> <code>mimic_weekly_sub_pro_transactions&nbsp;(1).csv</code> üzerinden yeniden kurulunca <strong>$29,12</strong>'ye indi. Diğer üç dosya hesaba hiç girmedi. Üç düzeltme rakamı aşağı çekti:</p>
  </div>
  <ol class="steps">
    <li><span class="n">1</span><div class="b"><b>Tam satır kopyası</b>
      <p>4.225 satırın 1'i birebir tekrar. Silindi → 4.224.</p></div></li>
    <li><span class="n">2</span><div class="b"><b>Sol-kesik 50 kullanıcı çıkarıldı</b>
      <p>Ödemesi olan ama <code>initial_purchase</code> kaydı olmayan kullanıcılar pencere <em>öncesinde</em> abone olmuş. İlk gözlenen ödemeleri aslında 5. ya da 12. haftaları olabilir; “1. dönem” sayılırlarsa hayatta kalma oranı yukarı şişer.</p></div></li>
    <li><span class="n">3</span><div class="b"><b>İptal sinyali sansür hesabına katıldı</b>
      <p>Son ödemesinden sonra <code>subscription_canceled</code> kaydı olan kullanıcı, gözlem penceresi dolmasa bile <strong>yenilemeyeceği kesin</strong> olan kişidir. Bu kural 30 kullanıcıyı “bilinmiyor”dan “churn etti”ye taşıdı — eskisi yukarı yanlıydı.</p></div></li>
  </ol>

  <div class="flag stop"><span class="t">Elendi</span><p>4. adımda önerilen <strong>sBG modeli test edildi ve reddedildi.</strong> Holdout kurdum (dönem 1–8 ile fit, 9–22 tahmin): sBG ortalama <strong>%42,6</strong> hata yaptı, 22. ödemede <strong>+%120</strong>; sabit plato %7,6. Bu veride yenileme oranı 5. dönemden sonra <em>yükselmiyor</em>, %92–93'te düzleşiyor — sBG'nin “hazard sürekli düşer” varsayımı geçersiz. Tek başına fit edilseydi LTV $42,84 çıkacaktı.</p></div>

  <h3>Payda kim? &mdash; iki senaryo</h3>
  <div class="col">
    <p>“Abone başına LTV” cümlesindeki <em>abone</em> tanımı rakamı doğrudan oynatıyor. Bu veride iki savunulabilir tanım var ve ikisini de raporlamak, birini seçip sessiz kalmaktan daha güçlü.</p>
  </div>

  <div class="cards">
    <div class="card">
      <h3>Senaryo A &middot; Ödeme yapmış abone</h3>
      <p style="font-size:13.5px;color:var(--ink-2);margin:8px 0 12px">En az bir kez para ödemiş kullanıcı. Vakanın “<em>subscribed at least 1 time</em>” ifadesinin en doğrudan karşılığı.</p>
      <dl class="kv">
        <dt>Payda</dt><dd>862 abone</dd>
        <dt>Beklenen ödeme</dt><dd>5,84</dd>
        <dt>LTV brüt</dt><dd><strong>$29,12</strong></dd>
        <dt>%95 bootstrap</dt><dd>$25,30 – $33,56</dd>
        <dt>Net ×0,70</dt><dd>$20,39</dd>
      </dl>
      <p style="font-size:12.5px;color:var(--ink-3);margin:12px 0 0"><strong>Ne zaman kullanılır:</strong> ödeyen kullanıcı sayısıyla çarpılacaksa. Soru 2'deki ROAS köprüsü (<code>dönüşen kullanıcı × LTV ÷ harcama</code>) bu paydayı ister.</p>
    </div>
    <div class="card">
      <h3>Senaryo B &middot; Abonelik kaydı açılmış herkes</h3>
      <p style="font-size:13.5px;color:var(--ink-2);margin:8px 0 12px">A'ya ek olarak, abonelik kaydı oluşmuş ama <strong>hiç ödeme üretmemiş</strong> 34 kullanıcı. Bunların 21'i install günüyle aynı gün iptal etmiş; toplam gelirleri $0.</p>
      <dl class="kv">
        <dt>Payda</dt><dd>896 kayıt</dd>
        <dt>Beklenen ödeme</dt><dd>5,61</dd>
        <dt>LTV brüt</dt><dd><strong>$28,02</strong></dd>
        <dt>A'ya göre</dt><dd>&minus;%3,8</dd>
        <dt>Net ×0,70</dt><dd>$19,61</dd>
      </dl>
      <p style="font-size:12.5px;color:var(--ink-3);margin:12px 0 0"><strong>Ne zaman kullanılır:</strong> huni metriğin “abonelik başlatma” ise. Ödemeye hiç dönüşmeyen başlatmaları da maliyetin karşısına koyar.</p>
    </div>
  </div>
  <p class="caption">Kural basit: <strong>hangi sayıyla çarpacaksan onun paydasını kullan.</strong> İki senaryo arasındaki $1,11'lik fark, plato varsayımının tek başına yarattığı oynamadan ($0,89) biraz büyük — yani ihmal edilebilir değil, ama analizin sonucunu da değiştirmiyor: her iki tanımda da ilk yenileme tek kaldıraç olarak kalıyor.</p>

  <div class="flag good"><span class="t">Trial yok</span><p>Bu dosyada <strong>hiç trial olayı yok</strong> — hesaptan dışlanan bir şey de yok. 4.224 satırın tamamı tek ürün (<code>mimic_weekly_sub_pro</code>), olaylar yalnızca <code>renewed</code> / <code>initial_purchase</code> / <code>subscription_canceled</code>. Trial'lar Soru 2'nin gelir dosyasında (<code>mimic_pro_yearly_3d_trial</code>) yaşıyor ve bu hesaba karıştırılmadı. Geliri $0 olan tek satır tipi <code>subscription_canceled</code>, o da dönem saymıyor.</p></div>

  <h3>Revize sonuç</h3>
  <div class="result">
    <div><div class="lab">1. → 2. ödeme</div><div class="big">46,7<span style="font-size:19px">%</span></div><div class="sub">%53,3 ilk yenilemede gidiyor</div></div>
    <div><div class="lab">Plato (dnm 5–20)</div><div class="big">92,3<span style="font-size:19px">%</span></div><div class="sub">sabit, yükselmiyor</div></div>
    <div><div class="lab">LTV &middot; Senaryo A</div><div class="big">$29,12</div><div class="sub">862 ödeyen abone</div></div>
    <div><div class="lab">LTV &middot; Senaryo B</div><div class="big">$28,02</div><div class="sub">896 abonelik kaydı</div></div>
  </div>
  <p class="caption">Naif hesap (toplam yenileme / toplam ödeme, sabit oran) $19,48 verir — gerçekleşen gelir/abone ile aynı. Sansür düzeltmesi LTV'yi <strong>%50 yukarı</strong> taşıyor; sunumda anlatılacak asıl şey bu fark.</p>

  <h3>Sağlamlık</h3>
  <div class="tw">
    <table>
      <thead><tr><th>Kontrol</th><th class="num">Sonuç</th><th>Yorum</th></tr></thead>
      <tbody>
        <tr><td>Risk penceresi 7 / 8 / 10 / 14 / 21 gün</td><td class="num">%46,7 – %46,8</td><td>Sansür düzeltmesi pencere seçimine duyarsız</td></tr>
        <tr><td>Bootstrap %95 GA (1.000 tekrar)</td><td class="num">$25,30 – $33,56</td><td>Kullanıcı düzeyinde yeniden örnekleme</td></tr>
        <tr><td>Plato &plusmn;5 puan</td><td class="num">$26,66 – $35,18</td><td>En büyük tek belirsizlik kaynağı</td></tr>
        <tr><td>52 yerine 49 dönem (takvim yılı)</td><td class="num">$29,02</td><td>Ödemelerin %97'si tam 7 gün arayla; fark ihmal edilebilir</td></tr>
        <tr><td>Ağustos kohortu tek başına</td><td class="num">$33,18</td><td>862'nin 188'i; %22 örneklemle <strong>+%14 yukarı</strong> yanlı</td></tr>
        <tr><td>Aralık kohortu r&#8321;</td><td class="num">%42,1</td><td>Ağu–Kas %47–52 &rarr; trafik kalitesi düşüyor</td></tr>
      </tbody>
    </table>
  </div>
  <p class="caption">Kohort kırılımı tek ortalama LTV'nin neyi sakladığını gösteriyor: aynı ürün, aynı fiyat, ama Aralık'ta gelen kullanıcı Eylül'de gelenden belirgin biçimde daha az yenilemiş.</p>
</section>

<!-- ============ SORU 2 ============ -->
<section>
  <p class="eyebrow">Soru 2 &middot; Ana soru</p>
  <h2>Kampanya performansının değerlendirilmesi</h2>

  <div class="qbox">
    <p class="eyebrow">Vakanın sorduğu</p>
    <ol>
      <li>Toplam harcamasına göre <strong>en kârlı</strong> kampanya hangisi (ROI)?</li>
      <li>Hangi kampanyanın <strong>CPI</strong>'ı daha düşük?</li>
      <li>Hangi kampanya <strong>install başına en çok free trial</strong> getiriyor?</li>
      <li>Kampanya performansını değerlendirirken başka hangi KPI'lara bakardın?</li>
    </ol>
  </div>

  <h3>Veri haritası — üç tablo nasıl birleşiyor</h3>
  <div class="col">
    <p>Vakanın asıl teknik sınavı burada. Veri bilerek üçe bölünmüş: <strong>para kimde olduğunu</strong> gelir tablosu biliyor, <strong>o kişinin nereden geldiğini</strong> install tablosu biliyor, <strong>o kanalın ne kadara mal olduğunu</strong> cost tablosu biliyor. Hiçbiri tek başına bir soruyu cevaplayamıyor — ve gelir tablosunda kampanya alanı <em>hiç yok</em>, yani doğrudan “kampanya × gelir” diye bir sorgu yazılamıyor. Köprü <code>user_id</code>.</p>
  </div>

  <div class="lineage">
    <div class="tbl">
      <div class="thead">
        <span class="tname"><span class="pfx">case_study_</span>revenue_dataset</span>
        <span class="grain">grain: olay</span>
        <div class="rowcount">686 satır · 326 kullanıcı<br>kullanıcı başına ~2,1 olay</div>
      </div>
      <ul class="fields">
        <li class="key">user_id <span class="ftag">anahtar</span></li>
        <li class="msr">revenue <span class="ftag">ölçü</span></li>
        <li>event_name</li>
        <li>product_id</li>
        <li>event_date</li>
        <li>purchase_id</li>
        <li>country</li>
        <li>install_date <span class="ftag">kopya</span></li>
      </ul>
    </div>

    <div class="link">
      <span class="lkey">user_id</span>
      <svg class="arrow" viewBox="0 0 100 11" preserveAspectRatio="none" aria-hidden="true">
        <line x1="2" y1="5.5" x2="88" y2="5.5" stroke="var(--accent)" stroke-width="1.5"/>
        <path d="M88 1.5 L97 5.5 L88 9.5 Z" fill="var(--accent)"/>
      </svg>
      <span class="lcard">çok → bir</span>
      <span class="lnote">83 kullanıcı eşleşmiyor</span>
    </div>

    <div class="tbl mid">
      <div class="thead">
        <span class="tname"><span class="pfx">case_study_</span>install_dataset</span>
        <span class="grain">grain: kullanıcı</span>
        <div class="rowcount">2.250 satır = 2.250 kullanıcı<br>tekrar yok</div>
      </div>
      <ul class="fields">
        <li class="key">user_id <span class="ftag">anahtar</span></li>
        <li class="key">media_source <span class="ftag">anahtar</span></li>
        <li class="key">campaign <span class="ftag">anahtar*</span></li>
        <li>install_date</li>
      </ul>
      <div style="padding:9px 14px 12px; border-top:1px solid var(--hair); font-size:11.5px; color:var(--ink-3); line-height:1.45">
        <strong style="color:var(--ochre)">*</strong> önce normalize:<br>
        <code style="font-size:11px">REGEXP_REPLACE(campaign, r'\\.mp4_\\d+$', '')</code>
      </div>
    </div>

    <div class="link">
      <span class="lkey">media_source<br>+ campaign</span>
      <svg class="arrow" viewBox="0 0 100 11" preserveAspectRatio="none" aria-hidden="true">
        <line x1="2" y1="5.5" x2="88" y2="5.5" stroke="var(--accent)" stroke-width="1.5"/>
        <path d="M88 1.5 L97 5.5 L88 9.5 Z" fill="var(--accent)"/>
      </svg>
      <span class="lcard">çok → çok</span>
      <span class="lnote">önce GROUP BY şart</span>
    </div>

    <div class="tbl">
      <div class="thead">
        <span class="tname"><span class="pfx">case_study_</span>cost_dataset</span>
        <span class="grain">grain: gün×kampanya</span>
        <div class="rowcount">20 satır · 3 kampanya<br>kampanya başına 5–10 satır</div>
      </div>
      <ul class="fields">
        <li class="key">media_source <span class="ftag">anahtar</span></li>
        <li class="key">campaign <span class="ftag">anahtar*</span></li>
        <li class="msr">cost <span class="ftag">ölçü</span></li>
        <li class="msr">impressions <span class="ftag">ölçü</span></li>
        <li>date</li>
      </ul>
    </div>
  </div>
  <p class="caption">Üç tablonun <strong>grain'i (satırın ne anlama geldiği) üç farklı seviyede</strong>: olay → kullanıcı → gün×kampanya. Join'in tehlikeli olmasının sebebi tam olarak bu; her adımda bir seviye yukarı çıkıyoruz.</p>

  <div class="flag stop" style="margin-top:20px">
    <span class="t">Fan-out</span>
    <p><strong>Cost tablosunu doğrudan join etme.</strong> Her kampanyanın 5–10 günlük satırı var; install tablosuna anahtarla bağlarsan her kurulum o satırların hepsiyle çarpışır. Ölçtüm: <strong>2.250 kurulum 13.368'e</strong> çıkıyor (5,9×) ve harcama aynı oranda şişiyor. Doğrusu: cost'u <em>önce</em> <code>GROUP BY media_source, campaign</code> ile tek satıra indir, <em>sonra</em> birleştir. Aynı kural install → gelir yönünde de geçerli: gelir olay seviyesinde, kullanıcı başına ~2,1 satır — kurulum saymadan önce <code>COUNT(DISTINCT user_id)</code> kullan.</p>
  </div>

  <h4>Birleştirme sırası</h4>
  <pre><span class="c">-- 1) her tabloyu KENDİ grain'inde topla, sonra birleştir</span>
<b>WITH</b> c <b>AS</b> (                                   <span class="c">-- gün × kampanya  ->  kampanya</span>
  <b>SELECT</b> media_source,
         <b>REGEXP_REPLACE</b>(campaign, r'\\.mp4_\\d+$', '') <b>AS</b> campaign,
         <b>SUM</b>(cost) <b>AS</b> cost, <b>SUM</b>(impressions) <b>AS</b> impressions
  <b>FROM</b> cost_dataset <b>GROUP BY</b> 1,2
),
i <b>AS</b> (                                   <span class="c">-- kullanıcı  ->  kampanya</span>
  <b>SELECT</b> media_source,
         <b>REGEXP_REPLACE</b>(campaign, r'\\.mp4_\\d+$', '') <b>AS</b> campaign,
         <b>COUNT</b>(<b>DISTINCT</b> user_id) <b>AS</b> installs
  <b>FROM</b> install_dataset <b>GROUP BY</b> 1,2
),
r <b>AS</b> (                                   <span class="c">-- olay  ->  kampanya  (user_id köprüsüyle)</span>
  <b>SELECT</b> i.media_source,
         <b>REGEXP_REPLACE</b>(i.campaign, r'\\.mp4_\\d+$', '') <b>AS</b> campaign,
         <b>SUM</b>(r.revenue)                                            <b>AS</b> revenue,
         <b>COUNT</b>(<b>DISTINCT IF</b>(r.event_name='trial_started', r.user_id, <b>NULL</b>)) <b>AS</b> trials,
         <b>COUNT</b>(<b>DISTINCT IF</b>(r.event_name <b>IN</b> ('trial_converted','initial_purchase'),
                              r.user_id, <b>NULL</b>))                  <b>AS</b> payers
  <b>FROM</b> revenue_dataset r
  <b>JOIN</b> install_dataset i <b>USING</b> (user_id)     <span class="c">-- INNER: atıfsız 83 kullanıcı burada düşer</span>
  <b>GROUP BY</b> 1,2
)
<b>SELECT</b> c.media_source, c.campaign, i.installs, c.cost,
       <b>SAFE_DIVIDE</b>(c.cost, i.installs)              <b>AS</b> cpi,
       <b>SAFE_DIVIDE</b>(r.trials, i.installs)            <b>AS</b> trial_per_install,
       <b>SAFE_DIVIDE</b>(r.revenue - c.cost, c.cost)      <b>AS</b> roi
<b>FROM</b> c <b>LEFT JOIN</b> i <b>USING</b> (media_source, campaign)
       <b>LEFT JOIN</b> r <b>USING</b> (media_source, campaign)
<b>ORDER BY</b> c.cost <b>DESC</b>;</pre>

  <p class="col">Haritayı kurduktan sonra üç yerde kayıp çıkıyor — üçü de sonucu değiştiriyor:</p>

  <div class="flag warn">
    <span class="t">Normalize</span>
    <p>Kampanya adları <strong>kreatif eki taşıyor</strong>: <code>MMC_133_EN_F_WEDNESDAY</code>, <code>…WEDNESDAY.mp4_001</code>, <code>…WEDNESDAY.mp4_002</code>, <code>…mp4_003</code> — dördü aynı kampanya. Birleştirmezsen TikTok'un 482 kurulumu dört parçaya bölünür ve maliyetle hiç eşleşmez. Ama <strong>fazla normalize etme</strong>: <code>MMC_133_EN_<u>F</u>_WEDNESDAY</code> ile <code>MMC_133_EN_<u>Y</u>_WEDNESDAY</code> ayrı kampanyalar, birleştirilmemeli. Kural sadece <code>.mp4_NNN</code> ekini siler.</p>
  </div>
  <div class="flag warn">
    <span class="t">Atıfsız</span>
    <p>Gelir tablosundaki <strong>326 kullanıcının 83'ü install tablosunda yok</strong> — 166 satır, <strong>$816,99 gelir (toplamın %27,1'i)</strong>. Install tablosunda hiç organik satır olmadığına göre bunlar organik/atıfsız kullanıcılar. Kampanya ROI'sine <em>dahil edilmemeli</em>, ama ayrı bir satır olarak raporlanmalı: ücretli kanal değerlendirmesinin dışında duran gerçek bir gelir kalemi bu.</p>
  </div>
  <div class="flag warn">
    <span class="t">Maliyetsiz</span>
    <p>Install tablosunda maliyet tablosunda <strong>bulunmayan 9 kampanya</strong> var (73 kurulum, %3,2). Hepsinin geliri $0. Bunlar CPI hesabında paydaya girerse maliyeti sıfır olan “bedava kurulum” yanılsaması yaratır — analiz dışı bırakılıp not düşülmeli. Ayrıca 16 satırda <code>campaign</code> alanı tamamen <strong>boş</strong>.</p>
  </div>
  <div class="flag good">
    <span class="t">Doğrulama</span>
    <p>Haritayı kurduktan sonra iki kontrol yaptım, ikisi de temiz çıktı: <strong>(1)</strong> hiçbir kampanya adı iki kanalda birden geçmiyor, yani tek başına <code>campaign</code> ile join etmek bu veride güvenli — yine de <code>(media_source, campaign)</code> çifti savunmacı seçim. <strong>(2)</strong> <code>install_date</code> hem gelir hem install tablosunda var ve eşleşen 520 satırın <strong>hiçbirinde uyuşmazlık yok</strong>; bu, <code>user_id</code> join'inin doğru kurulduğunun bağımsız kanıtı. Join yazdıktan sonra böyle bir çapraz kontrol her zaman aranmalı.</p>
  </div>

  <h3>Kampanya karnesi</h3>
  <p class="col">Maliyeti olan üç kampanya, normalize anahtar üzerinden. “Ödeyen” = trial'dan dönen veya doğrudan satın alan tekil kullanıcı.</p>
  <div class="tw">
    <table>
      <thead><tr>
        <th>Kampanya</th><th class="num">Install</th><th class="num">Harcama</th><th class="num">CPI</th>
        <th class="num">Trial</th><th class="num">Trial/install</th><th class="num">Trial→ödeme</th>
        <th class="num">Ödeyen</th><th class="num">Ödeyen maliyeti</th><th class="num">Gelir</th><th class="num">ROI</th><th class="num">1-yıl ROAS</th>
      </tr></thead>
      <tbody>
        <tr>
          <td><strong>MMC_107</strong><br><span style="color:var(--ink-3);font-size:12px">Facebook</span></td>
          <td class="num">1.593</td><td class="num">$1.356,89</td><td class="num">$0,85</td>
          <td class="num">170</td><td class="num">10,67%</td><td class="num">27,1%</td>
          <td class="num">89</td><td class="num">$15,25</td><td class="num">$2.023,83</td>
          <td class="num" style="color:var(--ok);font-weight:650">+49,2%</td><td class="num" style="color:var(--ok);font-weight:650">2,23×</td>
        </tr>
        <tr>
          <td><strong>MMC_018US_EN</strong><br><span style="color:var(--ink-3);font-size:12px">Facebook</span></td>
          <td class="num">102</td><td class="num">$77,81</td><td class="num">$0,76</td>
          <td class="num">11</td><td class="num">10,78%</td><td class="num">18,2%</td>
          <td class="num">5</td><td class="num">$15,56</td><td class="num">$94,93</td>
          <td class="num" style="color:var(--ok)">+22,0%</td><td class="num" style="color:var(--ok)">2,13×</td>
        </tr>
        <tr>
          <td><strong>MMC_133_EN_F_WEDNESDAY</strong><br><span style="color:var(--ink-3);font-size:12px">TikTok</span></td>
          <td class="num">482</td><td class="num">$90,23</td><td class="num" style="color:var(--ok);font-weight:650">$0,19</td>
          <td class="num">11</td><td class="num" style="color:var(--alert)">2,28%</td><td class="num">18,2%</td>
          <td class="num">3</td><td class="num" style="color:var(--alert)">$30,08</td><td class="num">$74,97</td>
          <td class="num" style="color:var(--alert);font-weight:650">−16,9%</td><td class="num">1,13×</td>
        </tr>
      </tbody>
      <tfoot><tr>
        <td>Toplam (atıflı)</td><td class="num">2.177</td><td class="num">$1.524,93</td><td class="num">$0,70</td>
        <td class="num">192</td><td class="num">8,82%</td><td class="num">26,0%</td>
        <td class="num">97</td><td class="num">$15,72</td><td class="num">$2.193,73</td><!--x-->
        <td class="num">+43,9%</td><td class="num">2,16×</td>
      </tr></tfoot>
    </table>
  </div>
  <p class="caption">ROI gerçekleşen gelirle (16 Oca'ya kadar). <strong>1-yıl ROAS</strong> tahmini değerdir: yıllık aboneye $34,99 (peşin, tam bir yıl), haftalık aboneye 1. sorudan gelen $31,99 LTV atanarak hesaplandı.</p>

  <h3>Cevaplar</h3>

  <h4>1 · En kârlı kampanya hangisi (ROI)?</h4>
  <div class="col">
    <p><strong>MMC_107 (Facebook).</strong> Hem oran hem hacim olarak tek gerçek kazanan: 5 günde $1.356,89 harcamış, 16 Ocak'a kadar $2.023,83 gelir üretmiş — <strong>%49,2 gerçekleşen ROI</strong>, tahmini 1 yıllık ROAS <strong>2,23×</strong>. Toplam harcamanın %89'unu ve toplam atıflı gelirin %92'sini tek başına taşıyor.</p>
    <p>MMC_018US_EN oransal olarak yakın (+%22 ROI, 2,13× ROAS) ama 5 ödeyen kullanıcıya dayanıyor; “kârlı” demek için çok küçük. <strong>MMC_133 (TikTok) zararda</strong>: −%16,9. Tahmini LTV ile bakınca 1,13×'e çıkıyor, yani başabaşın hemen üstünde — ama bu 3 ödeyen kullanıcının üzerine kurulu bir tahmin.</p>
  </div>

  <h4>2 · Hangi kampanyanın CPI'ı daha düşük?</h4>
  <div class="col">
    <p><strong>MMC_133 (TikTok): $0,19</strong> — Facebook'un dörtte biri. Vakanın sorduğu soru bu, cevabı da bu. Ama tek başına söylenirse yanlış karara götürür, çünkü ucuz install ucuz kullanıcı demek:</p>
  </div>
  <div class="flag stop">
    <span class="t">Tuzak</span>
    <p>TikTok install başına <strong>4,5× ucuz</strong>, ama <strong>ödeyen kullanıcı başına 2× pahalı</strong> ($30,08 vs $15,25). Kurulumların yalnızca %2,28'i trial başlatıyor — Facebook'ta bu oran %10,67. Bütçe kararı CPI'ya değil <strong>ödeyen kullanıcı maliyetine</strong> bakmalı; bu vakanın asıl sınavı burada.</p>
  </div>

  <h4>3 · Install başına en çok free trial getiren kampanya?</h4>
  <div class="col">
    <p>Ham sayıya bakılırsa <strong>MMC_018US_EN, %10,78</strong> ile ilk sırada; MMC_107 %10,67 ile hemen arkasında. Ama bu fark gerçek değil:</p>
  </div>
  <div class="tw">
    <table>
      <thead><tr><th>Kampanya</th><th class="num">Trial / install</th><th class="num">%95 güven aralığı</th><th>Yorum</th></tr></thead>
      <tbody>
        <tr><td>MMC_018US_EN</td><td class="num">10,78%</td><td class="num">6,13% – 18,29%</td><td>102 kurulum — aralık çok geniş</td></tr>
        <tr><td>MMC_107</td><td class="num">10,67%</td><td class="num">9,25% – 12,28%</td><td>1.593 kurulum — güvenilir</td></tr>
        <tr><td>MMC_133_EN_F_WEDNESDAY</td><td class="num">2,28%</td><td class="num">1,28% – 4,04%</td><td>diğer ikisinden <strong>kesin</strong> düşük</td></tr>
      </tbody>
    </table>
  </div>
  <p class="caption">İki Facebook kampanyasının güven aralıkları tamamen iç içe → <strong>istatistiksel olarak berabere</strong>. Doğru cevap “MMC_018US_EN” değil, “<strong>iki Facebook kampanyası eşit, TikTok anlamlı biçimde geride</strong>”. TikTok'un aralığı diğerleriyle hiç kesişmiyor; bu tek çıkarım güvenle yapılabilir.</p>

  <div class="flag warn">
    <span class="t">Ölçek</span>
    <p>Veri <strong>5 günlük</strong> ve gelir yalnızca 16 Ocak'a kadar gözlenmiş — 1 Ocak kurulumu 15, 5 Ocak kurulumu 11 gün izlenmiş. Haftalık aboneler bu pencerede en fazla 1–2 yenileme yapabildi. Bu yüzden gerçekleşen ROI <strong>alt sınırdır</strong>; kanal kararı LTV'li ROAS ile verilmeli.</p>
  </div>
  <div class="flag good">
    <span class="t">Köprü</span>
    <p>Vakanın iki sorusunu birbirine bağlayan yer burası: <strong>ROAS = (dönüşen kullanıcı × LTV) ÷ harcama</strong>. Haftalık abonenin LTV'si 1. sorudan geliyor ($31,99). Bu bağ kurulmadan 5 günlük veriyle bütçe dağıtımı yapılamaz — soru 2'nin “bütçeyi geliri maksimize edecek şekilde dağıt” hedefi ancak böyle cevaplanır.</p>
  </div>

  <h4>Bütçe önerisi</h4>
  <div class="col">
    <p>Bu veriyle savunulabilir tek öneri: <strong>MMC_107'ye ölçeklen</strong> (tek kanıtlanmış kârlı ve hacimli kampanya), <strong>MMC_018US_EN'i istatistiksel anlamlılığa ulaşana kadar kontrollü büyüt</strong> (aynı huni performansı, sadece küçük), <strong>MMC_133'ü mevcut kreatifle durdur</strong> — ucuz trafik alıyor ama ödeyen kullanıcıya çevirmiyor. TikTok'u tamamen kapatmak yerine, hedefleme/kreatif değişikliğiyle <em>trial oranını</em> ölçen yeni bir test kurmak daha doğru; sorun maliyet değil, kullanıcı kalitesi.</p>
    <p>Bir uyarı: MMC_107'yi ölçeklerken CPI'ın bozulacağını beklemek gerek. Günlük veri şimdiden bunu gösteriyor — harcama arttıkça CPI $0,77 ile $0,97 arasında dalgalanıyor. Ölçeklemeyi kademeli yapıp marjinal CPI'ı izlemek şart.</p>
  </div>

  <h3>4. şık: başka hangi KPI'lar</h3>
  <ul class="plain">
    <li><strong>Payback süresi</strong> — harcanan doların kaç günde geri döndüğü. Bütçe dağıtımını asıl belirleyen budur, ROI değil.</li>
    <li><strong>Kohort ROAS eğrisi</strong> (D7 / D30 / D90) — hangi kanalın geç olgunlaştığını gösterir; erken kesilen kampanyalar burada kurtulur.</li>
    <li><strong>Marjinal CPI / doygunluk</strong> — bütçe artınca CPI nasıl bozuluyor? “En iyi kampanya”ya sınırsız bütçe akıtmanın sınırını bu belirler.</li>
    <li><strong>Install → trial → ödeyen huni oranları</strong> — aynı CPI'da farklı kalitede kullanıcı gelebilir; huni bunu ayırır.</li>
    <li><strong>Retention D1/D7</strong> kanal bazında — kullanıcı kalitesinin en erken sinyali, gelir daha oluşmadan görülür.</li>
    <li><strong>İptal ve iade oranı</strong> kanal bazında — brüt geliri net gelire çeviren düzeltme.</li>
    <li><strong>CTR / IPM</strong> (install per mille) — kreatif yorgunluğunun erken göstergesi; CPM zaten elimizde.</li>
    <li><strong>Organik uplift</strong> — ücretli harcamanın organik installara etkisi; kanal kıyasında sistematik yanlılık kaynağı.</li>
  </ul>
</section>

<!-- ============ SORU 3 ============ -->
<section>
  <p class="eyebrow">Soru 3 &middot; Bonus</p>
  <h2>Churn analizi ve retention</h2>

  <div class="qbox">
    <p class="eyebrow">Vakanın sorduğu</p>
    <ol>
      <li>Mevcut ortalama <strong>D1 ve D7 kullanıcı retention</strong> oranı nedir?</li>
      <li>Genel retention nasıl iyileştirilir — hangi <strong>özellikler, davranışlar veya huni adımları</strong> churn'ü önlemek için önceliklendirilmeli?</li>
    </ol>
    <p style="margin-top:10px;color:var(--ink-3);font-size:14px">Veri: 1–5 Kasım 2022 arasında Mimic'i kuran kullanıcıların tüm event kayıtları. Vaka bu soruyu isteğe bağlı sayıyor.</p>
  </div>

  <div class="flag stop">
    <span class="t">Dürüstlük</span>
    <p><strong>D1 ve D7 retention bu veriyle hesaplanamaz.</strong> Kasım event dosyası elimizde yok; S2'nin üç tablosunda ise uygulama açılışı, oturum veya ekran olayı bulunmuyor — yalnızca para olayları var (<code>trial_started</code>, <code>trial_cancelled</code>, <code>trial_converted</code>, <code>initial_purchase</code>, <code>renewed</code>, <code>subscription_canceled</code>). Para olayı üretmeyi “aktiflik” saymak D1'i %10'lar seviyesinde gösterir ve <em>yanlış</em> olur. Rakam uydurmak yerine, aynı planın <strong>uygulanabilir adımlarını</strong> mevcut veriye uyguladım.</p>
  </div>

  <h3>Planın uygulanan kısmı — para hunisi</h3>
  <p class="col">Plandaki 3, 4, 5 ve 6. adımlar S2 verisiyle çalışıyor: huniyi çiz, en büyük düşüşü bul, zamanlamayı ölç, 1. soruyla köprü kur. 2.250 ücretli kurulumun izlediği yol:</p>

  <figure>
    <figcaption>Kurulumdan ödemeye</figcaption>
    <p class="figsub">1–5 Ocak 2023 · 2.250 ücretli kurulum · gelir 16 Ocak'a kadar gözlendi</p>
    <div class="funnel">
      <div class="fstep"><span class="fl">Ücretli kurulum</span><span class="fbar" style="--w:100%"></span><span class="fv">2.250</span><span class="fp">100%</span></div>
      <div class="fstep"><span class="fl">Yıllık trial başlattı</span><span class="fbar" style="--w:8.8%"></span><span class="fv">198</span><span class="fp">8,8%</span></div>
      <div class="fstep"><span class="fl">Trial'dan ödemeye döndü</span><span class="fbar" style="--w:2.22%"></span><span class="fv">50</span><span class="fp">2,2%</span></div>
      <div class="fstep"><span class="fl">Haftalık doğrudan satın aldı</span><span class="fbar" style="--w:2.04%"></span><span class="fv">46</span><span class="fp">2,0%</span></div>
      <div class="fstep"><span class="fl">Haftalıkta 2. haftaya geçti</span><span class="fbar" style="--w:0.89%"></span><span class="fv">20</span><span class="fp">0,9%</span></div>
    </div>
    <p class="caption" style="margin-top:14px">Adımlar birbirinin alt kümesi değil: yıllık trial hattı ile haftalık hat paralel iki yol. Yüzdeler kurulum tabanına göre.</p>
  </figure>

  <h4>En büyük düşüş nerede</h4>
  <div class="col">
    <p>Cevap tartışmasız: <strong>kurulum → trial.</strong> Kullanıcıların <strong>%91,2'si hiçbir para olayı üretmeden kayboluyor</strong>. Bunun yanında trial→ödeme kaybı (%74,7) küçük kalıyor, çünkü çok daha küçük bir havuzda gerçekleşiyor. Ürün tarafında ilk kaldıraç <em>paywall'ın kendisi değil, paywall'a kadar gelen kullanıcı oranı</em>.</p>
  </div>

  <h4>Zamanlama — asıl bulgu</h4>
  <div class="tw">
    <table>
      <thead><tr><th>Olay</th><th class="num">n</th><th class="num">Medyan gün</th><th>Ne söylüyor</th></tr></thead>
      <tbody>
        <tr><td><code>trial_started</code></td><td class="num">261</td><td class="num">0</td><td><strong>%95'i kurulum günü.</strong> Trial kararı ilk oturumda veriliyor; 1. günden sonra neredeyse hiç trial başlamıyor.</td></tr>
        <tr><td><code>initial_purchase</code></td><td class="num">63</td><td class="num">0</td><td>%87'si kurulum günü. Doğrudan satın alma da ilk oturuma bağlı.</td></tr>
        <tr><td><code>trial_cancelled</code></td><td class="num">207</td><td class="num">4</td><td><strong>%89'u trial'ın 3.–4. gününde</strong> — yani ücretlendirmeden hemen önce.</td></tr>
        <tr><td><code>trial_converted</code></td><td class="num">69</td><td class="num">3</td><td>Dönüşüm tam 3. günde, trial bitiminde otomatik gerçekleşiyor.</td></tr>
        <tr><td><code>subscription_canceled</code></td><td class="num">47</td><td class="num">7</td><td>İlk faturalama döngüsünün sonunda yığılıyor.</td></tr>
      </tbody>
    </table>
  </div>
  <div class="flag good">
    <span class="t">Bulgu</span>
    <p>Trial iptallerinin <strong>%48'i 3. günde, %89'u 4. günün sonunda</strong> geliyor. 3 günlük trial'ın bitimiyle bu tepe birebir çakışıyor. Bu, “ürünü beğenmedim” churn'ü değil — <strong>ücretlendirilmeden önce çıkma</strong> davranışı. Kullanıcı trial'ı ürünü denemek için değil, ücretsiz erişim için almış. Müdahale noktası trial'ın 3. günü değil, <strong>1. günü</strong>: kullanıcının o ilk oturumda ürünün değerini görüp görmediği her şeyi belirliyor.</p>
  </div>

  <h4>Kanal, kullanıcı kalitesini belirliyor</h4>
  <div class="tw">
    <table>
      <thead><tr><th>Kampanya</th><th class="num">Kurulum</th><th class="num">Trial oranı</th><th class="num">Trial→ödeme</th><th class="num">Ödeyen / kurulum</th></tr></thead>
      <tbody>
        <tr><td>MMC_107 <span style="color:var(--ink-3)">· Facebook</span></td><td class="num">1.593</td><td class="num">10,67%</td><td class="num">27,1%</td><td class="num" style="color:var(--ok);font-weight:650">5,59%</td></tr>
        <tr><td>MMC_018US_EN <span style="color:var(--ink-3)">· Facebook</span></td><td class="num">102</td><td class="num">10,78%</td><td class="num">18,2%</td><td class="num">4,90%</td></tr>
        <tr><td>MMC_133_EN_F <span style="color:var(--ink-3)">· TikTok</span></td><td class="num">482</td><td class="num">2,28%</td><td class="num">18,2%</td><td class="num" style="color:var(--alert);font-weight:650">0,62%</td></tr>
      </tbody>
    </table>
  </div>
  <p class="caption">Churn'ün bir kısmı ürün sorunu değil <strong>trafik sorunu</strong>: TikTok kullanıcısının ödeyen olma olasılığı Facebook kullanıcısının dokuzda biri. Retention'ı iyileştirmenin en hızlı yolu bazen özellik geliştirmek değil, kötü kohortu satın almayı bırakmaktır.</p>

  <h4>Soru 1 ile köprü — bağımsız doğrulama</h4>
  <div class="flag good">
    <span class="t">Tutarlı</span>
    <p>S2'de haftalık aboneliği doğrudan satın alan 46 kullanıcının <strong>20'si en az bir yenileme yaptı = %43,5</strong>. 1. sorudaki tamamen ayrı veri setinden (farklı dönem, farklı SKU, 911 kullanıcı) çıkan 1.→2. hafta yenileme oranı <strong>%47,6</strong> idi. İki bağımsız kaynak aynı yere düşüyor — S2'nin gözlem penceresi 11–15 gün olduğu için %43,5 hafif aşağı yanlı; düzeltilince fark daha da kapanır. Bu, LTV modelinin S2 kullanıcılarına da uygulanabileceğinin kanıtı.</p>
  </div>

  <h3>Öneriler <span style="font-weight:400;color:var(--ink-3);font-size:13px">— gözlem → etkilenen kitle → değişiklik → ölçülecek metrik</span></h3>
  <ol class="steps">
    <li><span class="n">1</span><div class="b"><b>Paywall'ı ilk çıktının arkasına al</b>
      <p><strong>Gözlem:</strong> kurulumların %91,2'si hiç trial başlatmıyor, trial kararı %95 oranında ilk oturumda veriliyor. <strong>Kitle:</strong> 2.052 / 2.250 kullanıcı. <strong>Değişiklik:</strong> kullanıcı ilk animasyonunu üretmeden paywall gösterme; değeri gördükten sonra sun. <strong>Metrik:</strong> trial oranı %8,8 → hedef %12+.</p></div></li>
    <li><span class="n">2</span><div class="b"><b>Trial'ın 2. gününe değer hatırlatması koy</b>
      <p><strong>Gözlem:</strong> iptallerin %89'u 3.–4. günde, ücretlendirmeden hemen önce. <strong>Kitle:</strong> 207 trial kullanıcısı. <strong>Değişiklik:</strong> 2. günde kullanıcının o ana kadar ürettiklerini gösteren bir bildirim + kullanılmamış premium özelliği tanıtan bir adım. <strong>Metrik:</strong> trial→ödeme %25,3 → hedef %32.</p></div></li>
    <li><span class="n">3</span><div class="b"><b>Haftalık abonelikte 1. haftaya müdahale et</b>
      <p><strong>Gözlem:</strong> hem S1 (%47,6) hem S2 (%43,5) 1.→2. hafta geçişinde kaybın yarıdan fazlasını gösteriyor; sonraki haftalarda kalma oranı %85–95. <strong>Kitle:</strong> her yeni haftalık abone. <strong>Değişiklik:</strong> ilk 7 gün içinde en az iki kez ürüne geri getiren tetikleyici (yeni efekt, hatırlatma). <strong>Metrik:</strong> 1.→2. hafta yenileme oranı; +5 puan artış LTV'yi $31,99'dan ~$34,8'e taşır.</p></div></li>
    <li><span class="n">4</span><div class="b"><b>Kanal bazlı kalite eşiği koy</b>
      <p><strong>Gözlem:</strong> TikTok kurulumunun ödeyene dönme oranı %0,62, Facebook'un %5,59. <strong>Kitle:</strong> 482 kurulum. <strong>Değişiklik:</strong> kampanya optimizasyonunu install yerine trial/purchase olayına çevir; kalite eşiğini tutmayan kreatifi durdur. <strong>Metrik:</strong> ödeyen kullanıcı maliyeti $30,08 → Facebook seviyesi olan $15 bandı.</p></div></li>
  </ol>

  <h3>Kasım dosyası geldiğinde kalan adımlar</h3>
  <div class="col">
    <p>Planın 1. ve 2. adımı hâlâ event verisine bağlı ve o dosyayla şu sırayla yapılacak:</p>
    <ul class="plain">
      <li><strong>Tanımı sabitle:</strong> D1 = kurulum gününden tam 1 takvim günü sonra en az bir event üreten kullanıcı oranı (klasik “day N”; rolling değil). Tanım raporun başına yazılır — D1 için sektörde üç farklı tanım dolaşıyor.</li>
      <li><strong>Kohort matrisi:</strong> 1–5 Kasım kurulumları günlük kohortlara ayrılır, <code>DATE_DIFF(event_date, install_date)</code> ile D0–D30 retention matrisi ve eğrisi çıkarılır.</li>
      <li><strong>Davranış karşılaştırması:</strong> D7'de kalanlar ile D1'de düşenler arasında ilk gün event sayısı, özellik çeşitliliği ve ilk çıktıya kadar geçen süre karşılaştırılır; anlamlı fark varsa <strong>“aha moment” eşiği</strong> önerilir.</li>
      <li><strong>Vakanın Notion event açıklamaları</strong> hesaba başlamadan önce okunur — hangi event'in “açılış” sayıldığı D1'i doğrudan değiştirir.</li>
    </ul>
    <p>Yukarıdaki dört öneri o veriyle ya doğrulanacak ya da düzeltilecek; huni bulguları hipotezleri şimdiden daraltıyor.</p>
  </div>

  <!-- ---------- SORU 3 REVIZYON: event dosyasi geldi ---------- -->
  <h3 id="s3-revizyon">Revizyon &middot; 30 Temmuz <span style="font-weight:400;color:var(--ink-3);font-size:13px">— eksik event dosyası geldi</span></h3>
  <div class="col">
    <p>Yukarıda “elimizde yok” denen dosya geldi: <code>case_study_mimic_event_list.csv</code> — <strong>37.920 satır, 1.153 kullanıcı, 24 event tipi</strong>, kurulum 1–5 Kasım 2022, gözlem D0–D70. Aşağıda altı sorgu ve her birinin çıktısı sırayla var. İlk üçü mevcut cevabı <strong>doğruluyor</strong>; son üçü manşeti <strong>değiştiriyor</strong>.</p>
  </div>

  <h3>1 &middot; Retention — D1 / D7 / D30</h3>
  <pre><span class="c">-- klasik "day N": kurulumdan tam N gun sonra en az bir event</span>
<b>WITH</b> ev <b>AS</b> (
  <b>SELECT</b> user_id,
         <b>DATE_DIFF</b>(<b>DATE</b>(event_timestamp), <b>DATE</b>(install_date), <b>DAY</b>) <b>AS</b> gun
  <b>FROM</b> mimic_event_list
)
<b>SELECT</b> gun,
       <b>COUNT</b>(<b>DISTINCT</b> user_id)                        <b>AS</b> aktif,
       <b>ROUND</b>(<b>COUNT</b>(<b>DISTINCT</b> user_id) / 1153 * 100, 2) <b>AS</b> pct
<b>FROM</b> ev <b>WHERE</b> gun <b>BETWEEN</b> 0 <b>AND</b> 30
<b>GROUP BY</b> gun <b>ORDER BY</b> gun;</pre>
  <div class="tw">
    <table>
      <thead><tr><th>gün</th><th class="num">aktif</th><th class="num">pct</th><th>Excel'de yazan</th></tr></thead>
      <tbody>
        <tr><td>D0</td><td class="num">1.152</td><td class="num">%99,91</td><td>1.152 ✓</td></tr>
        <tr><td><strong>D1</strong></td><td class="num"><strong>119</strong></td><td class="num"><strong>%10,32</strong></td><td>%10,3 ✓</td></tr>
        <tr><td>D2</td><td class="num">51</td><td class="num">%4,42</td><td>51 ✓</td></tr>
        <tr><td>D3</td><td class="num">30</td><td class="num">%2,60</td><td>30 ✓</td></tr>
        <tr><td>D5</td><td class="num">27</td><td class="num">%2,34</td><td>27 ✓</td></tr>
        <tr><td><strong>D7</strong></td><td class="num"><strong>14</strong></td><td class="num"><strong>%1,21</strong></td><td>%1,2 ✓</td></tr>
        <tr><td>D14</td><td class="num">9</td><td class="num">%0,78</td><td>9 ✓</td></tr>
        <tr><td><strong>D30</strong></td><td class="num"><strong>4</strong></td><td class="num"><strong>%0,35</strong></td><td>%0,3 ✓</td></tr>
      </tbody>
    </table>
  </div>
  <div class="flag good"><span class="t">Doğrulandı</span><p>Tüm retention rakamları <strong>birebir</strong>. Sektör bandı D1 %25–30 iken bu uygulama %10,3'te; D7 %1,2 ise fiilen dip. Rakam doğru, teşhis de doğru.</p></div>

  <h3>2 &middot; Huni — event başına tekil kullanıcı</h3>
  <pre><span class="c">-- her adim TEKIL kullanici; event sayisi degil</span>
<b>SELECT</b> event_name,
       <b>COUNT</b>(<b>DISTINCT</b> user_id)                        <b>AS</b> kullanici,
       <b>ROUND</b>(<b>COUNT</b>(<b>DISTINCT</b> user_id) / 1153 * 100, 1) <b>AS</b> pct
<b>FROM</b> mimic_event_list
<b>WHERE</b> event_name <b>IN</b> ('first_open','lnd_get_started','lnd_first_tutorial',
                     'lnd_paywall','lnd_preview','lnd_waiting_screen',
                     'lnd_result_display','lnd_save_success','paywall_purchase_success')
<b>GROUP BY</b> event_name <b>ORDER BY</b> kullanici <b>DESC</b>;</pre>
  <div class="tw">
    <table>
      <thead><tr><th>adım</th><th class="num">kullanıcı</th><th class="num">pct</th><th>düşüş</th></tr></thead>
      <tbody>
        <tr><td><code>first_open</code></td><td class="num">1.151</td><td class="num">%99,8</td><td>—</td></tr>
        <tr><td><code>lnd_get_started</code></td><td class="num">1.138</td><td class="num">%98,7</td><td>&minus;13</td></tr>
        <tr><td><code>lnd_first_tutorial</code></td><td class="num">1.086</td><td class="num">%94,2</td><td>&minus;52</td></tr>
        <tr><td><code>lnd_paywall</code></td><td class="num">1.071</td><td class="num">%92,9</td><td>&minus;15</td></tr>
        <tr><td><code>lnd_preview</code></td><td class="num">723</td><td class="num">%62,7</td><td style="color:var(--alert)"><strong>&minus;348</strong></td></tr>
        <tr><td><code>lnd_waiting_screen</code></td><td class="num">591</td><td class="num">%51,3</td><td>&minus;132</td></tr>
        <tr><td><code>lnd_result_display</code></td><td class="num">565</td><td class="num">%49,0</td><td>&minus;26</td></tr>
        <tr><td><code>lnd_save_success</code></td><td class="num">271</td><td class="num">%23,5</td><td>&minus;294</td></tr>
        <tr><td><code>paywall_purchase_success</code></td><td class="num">44</td><td class="num">%3,8</td><td>&minus;227</td></tr>
      </tbody>
    </table>
  </div>
  <p class="caption">9 adımın 9'u da Excel'dekiyle <strong>birebir</strong>. En büyük tek düşüş paywall'da değil, <strong>paywall → şarkı seçimi</strong> arasında: 348 kişi. Kohortun yalnızca <strong>%49'u</strong> ürünün ürettiği sonucu görüyor.</p>

  <h3>3 &middot; Huni gerçekten iç içe mi?</h3>
  <pre><span class="c">-- sunumdaki 845 / 528 dogru mu, ve ardisik adim mi?</span>
<b>WITH</b> u <b>AS</b> (
  <b>SELECT</b> user_id,
         <b>MAX</b>(event_name = 'lnd_paywall')          <b>AS</b> gordu,
         <b>MAX</b>(event_name = 'btn_paywall_cancel')   <b>AS</b> cancel,
         <b>MAX</b>(event_name = 'btn_paywall_continue') <b>AS</b> cont
  <b>FROM</b> mimic_event_list <b>GROUP BY</b> user_id
)
<b>SELECT</b> <b>COUNTIF</b>(gordu)                  <b>AS</b> paywall_goren,
       <b>COUNTIF</b>(cancel <b>AND NOT</b> cont)    <b>AS</b> sadece_cancel,
       <b>COUNTIF</b>(cont <b>AND NOT</b> cancel)    <b>AS</b> sadece_continue,
       <b>COUNTIF</b>(cancel <b>AND</b> cont)        <b>AS</b> her_ikisi,
       <b>COUNTIF</b>(gordu <b>AND NOT</b> cancel <b>AND NOT</b> cont) <b>AS</b> hicbiri
<b>FROM</b> u;</pre>
  <div class="result">
    <div><div class="lab">Paywall gören</div><div class="big">1.071</div><div class="sub">5.489 gösterim = kişi başı 5,1 kez</div></div>
    <div><div class="lab">Sadece cancel</div><div class="big">442</div><div class="sub">kapattı, hiç plan seçmedi</div></div>
    <div><div class="lab">Sadece continue</div><div class="big">125</div><div class="sub">plan seçti, hiç kapatmadı</div></div>
    <div><div class="lab">Her ikisi</div><div class="big">403</div><div class="sub">hem kapattı hem plan seçti</div></div>
  </div>
  <div class="flag stop"><span class="t">Düzeltme</span><p>Sunumdaki <strong>845</strong> (<code>btn_paywall_cancel</code>) ve <strong>528</strong> (<code>btn_paywall_continue</code>) rakamları <strong>doğru</strong> — ama ardışık huni adımı <strong>değil</strong>. 845 + 528 = 1.373 &gt; 1.071; <strong>403 kullanıcı ikisini de yapmış</strong>, çünkü paywall kişi başı 5,1 kez gösteriliyor. Aşağı oklarla çizilen dizilim, var olmayan bir alt-küme ilişkisi ima ediyor. Paralel iki kol olarak çizilmeli.</p></div>

  <h3>4 &middot; Paywall gerçekten negatif sinyal mi?</h3>
  <pre><span class="c">-- manşetin dayandigi karsilastirma: paywall goren vs gormeyen</span>
<b>WITH</b> u <b>AS</b> (
  <b>SELECT</b> user_id,
         <b>MAX</b>(event_name = 'lnd_paywall') <b>AS</b> paywall,
         <b>MAX</b>(<b>DATE_DIFF</b>(<b>DATE</b>(event_timestamp), <b>DATE</b>(install_date), <b>DAY</b>) = 1) <b>AS</b> d1
  <b>FROM</b> mimic_event_list <b>GROUP BY</b> user_id
)
<b>SELECT</b> paywall, <b>COUNT</b>(*) <b>AS</b> kisi, <b>COUNTIF</b>(d1) <b>AS</b> donen,
       <b>ROUND</b>(<b>COUNTIF</b>(d1) / <b>COUNT</b>(*) * 100, 1) <b>AS</b> d1_pct
<b>FROM</b> u <b>GROUP BY</b> paywall;</pre>
  <div class="tw">
    <table>
      <thead><tr><th>grup</th><th class="num">kişi</th><th class="num">D1 dönen</th><th class="num">D1 oranı</th></tr></thead>
      <tbody>
        <tr><td>Paywall <strong>görmedi</strong></td><td class="num">82</td><td class="num">5</td><td class="num">%6,1</td></tr>
        <tr><td>Paywall <strong>gördü</strong></td><td class="num">1.071</td><td class="num">114</td><td class="num">%10,6</td></tr>
        <tr><td colspan="3"><strong>ham odds ratio</strong></td><td class="num"><strong>1,83</strong></td></tr>
      </tbody>
    </table>
  </div>
  <div class="flag stop"><span class="t">Manşet çürüyor</span><p>Sunum <em>“paywall tek negatif sinyal”</em> diyor ve <strong>OR 0,35</strong> gösteriyor. Ham veride ilişki <strong>ters yönde: 1,83</strong> — paywall görenler daha çok dönüyor. Sadece D0 event'leriyle bakınca ham OR <strong>0,97</strong>, yani <strong>hiç etki yok</strong>. 0,35 çok değişkenli modelden gelen <em>düzeltilmiş</em> katsayı ve kontrol eklenince işaret dönüyor.</p></div>

  <h3>5 &middot; Neden dönüyor — paywall bir dallanma noktası değil</h3>
  <pre><span class="c">-- sonucu goren herkes paywall'i ONCE mi gormus?</span>
<b>WITH</b> t <b>AS</b> (
  <b>SELECT</b> user_id,
         <b>MIN</b>(<b>IF</b>(event_name='lnd_paywall',        event_timestamp, <b>NULL</b>)) <b>AS</b> pw,
         <b>MIN</b>(<b>IF</b>(event_name='lnd_result_display', event_timestamp, <b>NULL</b>)) <b>AS</b> res
  <b>FROM</b> mimic_event_list <b>GROUP BY</b> user_id
)
<b>SELECT</b> <b>COUNT</b>(*) <b>AS</b> sonuc_goren,
       <b>COUNTIF</b>(pw &lt; res) <b>AS</b> paywall_once,
       <b>COUNTIF</b>(pw <b>IS NULL</b>) <b>AS</b> paywall_hic_gormemis
<b>FROM</b> t <b>WHERE</b> res <b>IS NOT NULL</b>;</pre>
  <div class="result">
    <div><div class="lab">Sonucu gören</div><div class="big">565</div><div class="sub">kohortun %49'u</div></div>
    <div><div class="lab">Paywall önce</div><div class="big">565</div><div class="sub">istisnasız</div></div>
    <div><div class="lab">Oran</div><div class="big">%100</div><div class="sub">varyans sıfır</div></div>
    <div><div class="lab">Karşılaştırma</div><div class="big">82</div><div class="sub">paywall'ı hiç görmeyen</div></div>
  </div>
  <p class="caption">Paywall bir dallanma noktası değil, <strong>zorunlu turnike</strong>. Kohortun %92,9'u geçiyor ve sonucu görenlerin %100'ü önce oradan geçmiş. Yani <em>“paywall gördü”</em> pratikte <em>“uygulamayı gerçekten açtı”</em> ile eşanlamlı; katsayısı 82 kişilik, uygulamayı saniyeler içinde terk etmiş bir gruba karşı ölçülüyor. <strong>Bu değişken bu veriden tanımlanamaz.</strong></p>
  <div class="flag warn"><span class="t">Sonuç</span><p>“Paywall'ı ilk çıktının arkasına al” önerisi <strong>bu veriyle test edilemez</strong> — sıralamada varyans yok, karşılaştırılacak grup yok. Öneri iyi ürün sezgisi, ama kanıtı yok; doğru adım A/B testi. Bunu <em>söylemek</em> zayıflık değil, en güçlü hamle.</p></div>

  <h3>6 &middot; Hangi sinyal gerçekten sağlam?</h3>
  <pre><span class="c">-- her D0 davranisi icin: ham OR ve modelle duzeltilmis OR yan yana</span>
<b>WITH</b> d0 <b>AS</b> (
  <b>SELECT</b> user_id,
         <b>MAX</b>(<b>IF</b>(<b>DATE_DIFF</b>(<b>DATE</b>(event_timestamp),<b>DATE</b>(install_date),<b>DAY</b>)=0
                <b>AND</b> event_name='lnd_save_success', 1, 0)) <b>AS</b> kaydetti,
         <span class="c">-- ... diger D0 degiskenleri ayni kalipla</span>
         <b>MAX</b>(<b>IF</b>(<b>DATE_DIFF</b>(<b>DATE</b>(event_timestamp),<b>DATE</b>(install_date),<b>DAY</b>)=1,1,0)) <b>AS</b> d1
  <b>FROM</b> mimic_event_list <b>GROUP BY</b> user_id
)
<b>SELECT</b> kaydetti, <b>COUNT</b>(*) <b>AS</b> n, <b>AVG</b>(d1) <b>AS</b> d1_orani <b>FROM</b> d0 <b>GROUP BY</b> kaydetti;
<span class="c">-- duzeltilmis OR: ayni tablo uzerinde lojistik regresyon (D0 event sayisi,</span>
<span class="c">--                session sayisi, sure + asagidaki ikili degiskenler)</span></pre>
  <div class="tw">
    <table>
      <thead><tr><th>D0 davranışı</th><th class="num">n</th><th class="num">ham OR</th><th class="num">düzeltilmiş OR</th><th>dayanıklılık</th></tr></thead>
      <tbody>
        <tr><td><strong>Sonucu kaydetti</strong></td><td class="num">255</td><td class="num"><strong>3,73</strong></td><td class="num"><strong>1,52</strong></td><td style="color:var(--ok)"><strong>SAĞLAM</strong> — iki yönde de aynı</td></tr>
        <tr><td>Preview gördü</td><td class="num">692</td><td class="num">2,74</td><td class="num">1,52</td><td style="color:var(--ok)">sağlam</td></tr>
        <tr><td>Hata aldı (<code>mimic_error</code>)</td><td class="num">7</td><td class="num">0,00</td><td class="num">0,45</td><td style="color:var(--ok)">sağlam, ama n=7</td></tr>
        <tr><td>Sonucu gördü</td><td class="num">532</td><td class="num">2,53</td><td class="num">0,83</td><td style="color:var(--alert)">İŞARET DÖNÜYOR</td></tr>
        <tr><td>Boş arama</td><td class="num">95</td><td class="num">1,57</td><td class="num">0,95</td><td style="color:var(--alert)">işaret dönüyor</td></tr>
        <tr><td>Plan seçti</td><td class="num">497</td><td class="num">1,24</td><td class="num">0,96</td><td style="color:var(--alert)">işaret dönüyor</td></tr>
        <tr><td><strong>Paywall gördü</strong></td><td class="num">1.049</td><td class="num">0,97</td><td class="num">0,72</td><td style="color:var(--ink-3)">ham etki zaten sıfır</td></tr>
      </tbody>
    </table>
  </div>
  <p class="caption">İşaret dönmeleri bir hata değil, <strong>veriye içkin</strong>: D0 davranış değişkenlerinin hepsi aynı gizli “engagement” faktörünün altında ve birbirine kilitli. Kendi bağımsız modelimde de aynı dönmeler çıktı (AUC 0,738 in-sample; Excel'in 5-fold CV'si 0,689 — tutarlı). Bu yüzden <strong>tek bir katsayıya dayalı nedensel iddia kurulamaz</strong>; sadece iki yönde de sabit kalanlar konuşulabilir.</p>

  <h3>Manşet nasıl değişmeli</h3>
  <div class="cards">
    <div class="card">
      <h3 style="color:var(--alert)">Şu anki cümle</h3>
      <p style="font-size:14px;margin:10px 0 0"><em>“The paywall is the only negative signal in the model.”</em></p>
      <p style="font-size:12.5px;color:var(--ink-3);margin:12px 0 0">Ham veri tersini söylüyor (OR 1,83), karşılaştırma grubu 82 kişi, değişken kohortun %92,9'unda var. Mülakatta en saldırıya açık cümle bu.</p>
    </div>
    <div class="card">
      <h3 style="color:var(--ok)">Önerilen cümle</h3>
      <p style="font-size:14px;margin:10px 0 0"><em>“Kohortun yarısı ürünün ne yaptığını hiç görmüyor — ve dönüşü öngören tek sağlam davranış bir sonucu <strong>kaydetmek</strong>.”</em></p>
      <p style="font-size:12.5px;color:var(--ink-3);margin:12px 0 0">1.071 paywall'a varıyor, 565'i (%49) sonuç görüyor, 271'i (%23,5) kaydediyor. Kaydetme OR'ı ham 3,73 / düzeltilmiş 1,52 — kontrol ekleyince de ayakta. Aynı aksiyona çıkıyor, ama savunulabilir.</p>
    </div>
  </div>
  <p class="caption">Geri çekilen bir eleştiri: gün 16–21 arasındaki retention tümseği incelendi — 83 kullanıcı / 6 gün, app sürümü ve event dağılımı komşu günlerle aynı, günlük sayılar zaten 5–29 arasında oynuyor. <strong>Gürültü, sorun değil.</strong></p>
</section>

<!-- ============ SIRADAKİ ============ -->
<section>
  <p class="eyebrow">Sıradaki adımlar</p>
  <h2>Ne yapılacak, hangi sırayla</h2>

  <div class="tw">
    <table>
      <thead><tr><th>#</th><th>Adım</th><th>Neden şimdi</th><th>Durum</th></tr></thead>
      <tbody>
        <tr><td class="num">1</td><td>Soru 1 · censoring düzeltmeli LTV</td><td>ana yöntem, sonuç $31,99</td><td style="color:var(--ok)">bitti</td></tr>
        <tr><td class="num">2</td><td>Soru 2 · üç tablo join + kampanya karnesi</td><td>ROI, CPI, trial/install üçü de cevaplandı</td><td style="color:var(--ok)">bitti</td></tr>
        <tr><td class="num">3</td><td>Soru 3 · para hunisi churn analizi</td><td>event dosyası olmadan yapılabilen kısım</td><td style="color:var(--ok)">bitti</td></tr>
        <tr><td class="num">4</td><td>sBG modelini fit et, ampirik eğriyle karşılaştır</td><td>LTV projeksiyonunu ikinci yöntemle doğrular</td><td style="color:var(--ochre)">yöntem hazır</td></tr>
        <tr><td class="num">5</td><td>Kurulum ayına göre LTV kırılımı</td><td>tek ortalama LTV yanıltıcı mı, onu gösterir</td><td style="color:var(--ochre)">yöntem hazır</td></tr>
        <tr><td class="num">6</td><td>Q2 Notion açıklama sayfasını oku</td><td>alan tanımlarımızı teyit etmek için</td><td style="color:var(--ochre)">açık</td></tr>
        <tr><td class="num">7</td><td>Q3 event verisini indir → D1/D7</td><td>bonusun tek eksik parçası</td><td style="color:var(--alert)">bekliyor</td></tr>
        <tr><td class="num">8</td><td>Sunumu derle</td><td>vaka formatı serbest bırakıyor</td><td style="color:var(--ink-3)">son adım</td></tr>
      </tbody>
    </table>
  </div>

  <div class="deliver">
    <h4 style="margin-top:0">Teslim formatı</h4>
    <p style="margin-bottom:10px">Vaka “yazılı rapor, tablo, sunum — rahat ettiğin herhangi bir format” diyor ve amacını açıkça yazıyor: <em>“yaklaşımını, bakış açını ve güçlü yanlarını anlamak.”</em> Yani doğru cevaptan çok <strong>muhakemenin görünürlüğü</strong> puanlanıyor.</p>
    <p style="margin-bottom:0">Önerim: <strong>10–12 slaytlık sunum + arkasında çalışan SQL eki.</strong> Her soru için bir “yöntem” slaytı, bir “sonuç” slaytı, sonda bir “veri kalitesi ve varsayımlar” slaytı. Yukarıda bulduğum yinelenen <code>purchase_id</code>'ler, eksik <code>initial_purchase</code>'lar, kampanya adı normalizasyonu ve atıfsız $816,99 tam olarak o son slayta ait — ve çoğu adayın atlayacağı yer orası. Üç soruyu birbirine bağlayan iki bulgu (LTV → ROAS köprüsü ve iki bağımsız veri setinin aynı yenileme oranını vermesi) sunumun omurgası olmalı.</p>
  </div>
</section>

<footer>
  Kaynak: <span class="mono">Data Analyst.pdf</span> (10 sayfa) &middot; <span class="mono">mimic_weekly_sub_pro_transactions2.csv</span> &middot; <span class="mono">case_study_install_dataset.csv</span> &middot; <span class="mono">case_study_revenue_dataset.csv</span> &middot; <span class="mono">case_study_cost_dataset.csv</span><br>
  Tüm sayısal bulgular iki CSV üzerinde doğrudan hesaplandı; LTV rakamı 52 haftalık projeksiyondur, gözlenen değer değil.
</footer>

</div>

<script>
(function(){
  var S=[[1,1],[2,.4764],[3,.3957],[4,.3368],[5,.3074],[6,.276],[7,.2485],[8,.2372],[9,.2237],[10,.2108],[11,.1874],[12,.1734],[13,.1689],[14,.1559],[15,.1448],[16,.139],[17,.129],[18,.1204],[19,.1121],[20,.1043],[21,.0971],[22,.0904],[23,.0841],[24,.0783],[25,.0729],[26,.0678],[27,.0631],[28,.0588],[29,.0547],[30,.0509],[31,.0474],[32,.0441],[33,.0411],[34,.0382],[35,.0356],[36,.0331],[37,.0308],[38,.0287],[39,.0267],[40,.0248],[41,.0231],[42,.0215],[43,.02],[44,.0186],[45,.0174],[46,.0162],[47,.015],[48,.014],[49,.013],[50,.0121],[51,.0113],[52,.0105]];
  var OBS=18; // son gözlenen hafta
  var W=720,H=300,L=44,R=16,T=14,B=34, PW=W-L-R, PH=H-T-B;
  var x=function(w){return L+(w-1)/51*PW;}, y=function(v){return T+PH-v*PH;};
  var NS='http://www.w3.org/2000/svg';
  var svg=document.getElementById('chart');
  function el(n,a){var e=document.createElementNS(NS,n);for(var k in a)e.setAttribute(k,a[k]);return e;}

  // grid + y ekseni
  [0,.25,.5,.75,1].forEach(function(v){
    svg.appendChild(el('line',{x1:L,x2:W-R,y1:y(v),y2:y(v),stroke:'var(--hair)','stroke-width':1}));
    var t=el('text',{x:L-9,y:y(v)+4,'text-anchor':'end',fill:'var(--ink-3)','font-size':11});
    t.textContent=(v*100)+'%'; svg.appendChild(t);
  });
  // x ekseni
  [1,8,16,24,32,40,52].forEach(function(w){
    var t=el('text',{x:x(w),y:H-12,'text-anchor':'middle',fill:'var(--ink-3)','font-size':11});
    t.textContent='hf '+w; svg.appendChild(t);
  });

  function path(from,to){
    return S.slice(from,to).map(function(p,i){return (i?'L':'M')+x(p[0]).toFixed(1)+' '+y(p[1]).toFixed(1);}).join(' ');
  }
  // dolgu (gözlenen kısım)
  var fill=path(0,OBS)+' L'+x(S[OBS-1][0]).toFixed(1)+' '+y(0)+' L'+x(1)+' '+y(0)+' Z';
  svg.appendChild(el('path',{d:fill,fill:'var(--accent-soft)',stroke:'none'}));
  svg.appendChild(el('path',{d:path(0,OBS),fill:'none',stroke:'var(--accent)','stroke-width':2,'stroke-linejoin':'round','stroke-linecap':'round'}));
  svg.appendChild(el('path',{d:path(OBS-1,52),fill:'none',stroke:'var(--accent)','stroke-width':2,'stroke-dasharray':'5 4','stroke-linecap':'round',opacity:.62}));

  // vurgulanan noktalar + doğrudan etiket
  [[2,'%47,6','start',10,-9],[52,'%1,1','end',-4,-10]].forEach(function(m){
    var p=S[m[0]-1];
    svg.appendChild(el('circle',{cx:x(p[0]),cy:y(p[1]),r:4.5,fill:'var(--accent)',stroke:'var(--surface)','stroke-width':2}));
    var t=el('text',{x:x(p[0])+m[3],y:y(p[1])+m[4],'text-anchor':m[2],fill:'var(--ink)','font-size':12,'font-weight':650});
    t.textContent=m[1]; svg.appendChild(t);
  });

  // hover katmanı
  var cross=el('line',{x1:0,x2:0,y1:T,y2:T+PH,stroke:'var(--accent-line)','stroke-width':1,opacity:0});
  var dot=el('circle',{r:5,fill:'var(--accent)',stroke:'var(--surface)','stroke-width':2,opacity:0});
  svg.appendChild(cross); svg.appendChild(dot);
  var hit=el('rect',{x:L,y:T,width:PW,height:PH,fill:'transparent'});
  svg.appendChild(hit);
  var tip=document.getElementById('tip'), fig=svg.parentNode;

  function move(ev){
    var r=svg.getBoundingClientRect();
    var px=(ev.clientX-r.left)/r.width*W;
    var w=Math.round((px-L)/PW*51)+1; w=Math.max(1,Math.min(52,w));
    var p=S[w-1];
    cross.setAttribute('x1',x(w)); cross.setAttribute('x2',x(w)); cross.setAttribute('opacity',1);
    dot.setAttribute('cx',x(w)); dot.setAttribute('cy',y(p[1])); dot.setAttribute('opacity',1);
    tip.innerHTML='<b>Hafta '+w+(w<=OBS?'':' · projeksiyon')+'</b>'+
      'Hâlâ ödeyen: '+(p[1]*100).toFixed(1).replace('.',',')+'%';
    tip.style.opacity=1;
    var lx=x(w)/W*r.width, ly=y(p[1])/H*r.height;
    tip.style.left=Math.min(Math.max(lx+14,8), r.width-tip.offsetWidth-8)+'px';
    tip.style.top=Math.max(ly-14,8)+'px';
  }
  hit.addEventListener('mousemove',move);
  hit.addEventListener('pointermove',move);
  hit.addEventListener('mouseleave',function(){
    tip.style.opacity=0; cross.setAttribute('opacity',0); dot.setAttribute('opacity',0);
  });
})();
</script>

</body>
</html>`;

export default function MimicCaseReport() {
  const frameRef = useRef(null);
  const [ready, setReady] = useState(false);

  const printReport = useCallback(() => {
    const w = frameRef.current?.contentWindow;
    if (!w) return;
    w.focus();
    w.print();
  }, []);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#F2F5F6' }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
          padding: '10px 20px', background: '#FFFFFF', borderBottom: '1px solid #D7E0E3',
          position: 'sticky', top: 0, zIndex: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <a
            href="/"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, color: '#6D8087',
              fontSize: 13, textDecoration: 'none', flex: '0 0 auto',
            }}
          >
            <ArrowLeft size={15} /> Panele dön
          </a>
          <span style={{ color: '#C2CFD3' }}>|</span>
          <span
            style={{
              fontSize: 13.5, fontWeight: 600, color: '#111A1E',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}
          >
            Mimic — Data Analyst Case
          </span>
        </div>
        <button
          type="button"
          onClick={printReport}
          disabled={!ready}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '7px 14px', borderRadius: 3, border: '1px solid #0C5C6B',
            background: ready ? '#0C5C6B' : '#8FA9AF', color: '#fff',
            fontSize: 13, fontWeight: 600, cursor: ready ? 'pointer' : 'default',
            flex: '0 0 auto',
          }}
        >
          <Printer size={15} /> PDF indir
        </button>
      </div>

      <iframe
        ref={frameRef}
        title="Mimic Data Analyst Case raporu"
        srcDoc={REPORT_HTML}
        onLoad={() => setReady(true)}
        style={{ flex: '1 1 auto', width: '100%', border: 0, minHeight: 'calc(100vh - 49px)' }}
      />
    </div>
  );
}
